import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import MultiLineTruckManager from "./MultiLineTruckManager";
// ChatLauncher removed per request
import { cn } from "../lib/utils";
import {
  ShoppingCart,
  Code2,
  Settings,
  Truck,
  LogOut,
  LayoutDashboard,
  Users,
  AlertTriangle,
  Palette,
  MessageSquare,
  Loader2,
  TrendingUp,
  Package,
  Gauge,
  ChevronDown,
  FileSpreadsheet,
  ScanLine,
  Check,
  X,
  MapPin,
  Weight,
  Ruler,
  Search,
  Lock
} from "lucide-react";
import FeedbackFormInline from './FeedbackFormInline';
import { SidebarProvider, Sidebar, SidebarInset } from "./ui/sidebar";
import LoaderPlanView from './LoaderPlanView';
import NewLoaderPlanView from './NewLoaderPlanView';
import { AppSidebar } from "./AppSidebar";
import { Separator } from "./ui/separator";
import StatsCard from './StatsCard';
import DashboardHeader from './DashboardHeader';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./ui/tooltip";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import PalletClassificationPage from "../pages/PalletClassificationPage.jsx";
import PalletScannerPage from "../pages/PalletScannerPage.jsx";
import { supabase, SUPABASE_ENABLED } from '../lib/supabaseClient';

// Vehicle dimension constants (meters)
// These align with the 3D scene (TruckLoadingScene) which uses meters (e.g. trailer depth 12, truck depth 8.4)
const VEHICLE_DIMENSIONS = {
  truck: { width: 2.6, depth: 8.4, height: 2.6 },
  trailer: { width: 2.6, depth: 12.0, height: 2.6 }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeInOut" } },
};

export default function DeviasKit() {
  const { user, logout, isAuthenticated } = useAuth();
  const {
    getSavedLoadsByDateRange,
    getDailyStats: getDailyStatsRange,
    saveCurrentLoad,
    selectedLine: dcSelectedLine,
    selectedTruck: dcSelectedTruck,
  apiService: dataApi,
  // Persisted global state and actions
  drivers: dcDrivers,
  driverAssignments: dcDriverAssignments,
  connections: dcConnections,
  addDriver: dcAddDriver,
  removeDriver: dcRemoveDriver,
  assignDriverToTruck: dcAssignDriverToTruck,
  addConnection: dcAddConnection,
  removeConnection: dcRemoveConnection,
  } = useData();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const [totalUnplaced, setTotalUnplaced] = useState(0);
  const [yesterdayVolume, setYesterdayVolume] = useState(null);
  const [isDark, setIsDark] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  // Pallet Management controls wired to TruckLoadingScene
  const sceneRef = useRef(null);
  const importFileInputRef = useRef(null);
  const [pmVehicleType, setPmVehicleType] = useState('trailer');
  const [pmShowDangerousOnly, setPmShowDangerousOnly] = useState(false);
  const [pmShowGrid, setPmShowGrid] = useState(true);
  const [pmPalletName, setPmPalletName] = useState("");
  const [pmWidth, setPmWidth] = useState(0.8);
  const [pmDepth, setPmDepth] = useState(1.2);
  const [pmHeight, setPmHeight] = useState(0.5);
  const [pmWeight, setPmWeight] = useState(100);
  const [pmAttribute, setPmAttribute] = useState("none");
  const [pmNotToploadable, setPmNotToploadable] = useState(false);
  const [pmQuantity, setPmQuantity] = useState(1);
  const [selectedPalletId, setSelectedPalletId] = useState(null);
  
  // Import dropdown and scanner modal state
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showScannerImportModal, setShowScannerImportModal] = useState(false);
  const [scannedPallets, setScannedPallets] = useState([]);
  const [selectedScannedPallets, setSelectedScannedPallets] = useState(new Set());
  const [scannerSearchQuery, setScannerSearchQuery] = useState('');
  const [loadingScannedPallets, setLoadingScannedPallets] = useState(false);
  const importDropdownRef = useRef(null);

  // ── Lodia Transport Performance Dashboard State ──
  // Dashboard view mode: 'all' (all trucks) or 'line' (only active line)
  const [dashboardViewMode, setDashboardViewMode] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  // Removed refresh interval selector (auto-refresh disabled)
  const [refreshInterval] = useState("manual");
  const [filters, setFilters] = useState({
    depot: "",
    vehicleType: "", // "solo", "trailer"
    carrier: ""
  });

  // ── Lifted multi-line state (keeping for truck loading tab) ──
  const [lines, setLines] = useState([
    // Default lines renamed from specific city names to generic City 1 / City 2
    { id: "line-lulea", name: "City 1", trucks: [] },
    { id: "line-umea", name: "City 2", trucks: [] },
  ]);
  const [activeLineId, setActiveLineId] = useState(lines[0].id);
  // Local UI highlight for active vehicle in current line
  const [activeVehicleIndex, setActiveVehicleIndex] = useState(0);

  // ── Row weights state for integration with solver ──
  const [globalRowWeights, setGlobalRowWeights] = useState({});
  // Persist unplaced across tab switches by lifting state here
  const [unplacedByLine, setUnplacedByLine] = useState(() => {
    const init = {};
    (lines || []).forEach(l => { init[l.id] = []; });
    return init;
  });
  // Track if we hydrated from a local snapshot to avoid baseline resets
  const usedLocalSnapshotRef = useRef(false);
  // Keep unplaced map keys in sync with lines
  useEffect(() => {
    setUnplacedByLine(prev => {
      const next = { ...prev };
      (lines || []).forEach(l => { if (!next[l.id]) next[l.id] = []; });
      return next;
    });
  }, [lines]);

  // ── Local persistence (fallback when backend loads are empty or user unauthenticated) ──
  // Build a per-user, per-date key
  const userKey = (user?.id || user?.email || 'anon');
  const storageKey = `ml_snapshot_v2:${userKey}:${selectedDate}`;
  const fallbackAnonKey = `ml_snapshot_v2:anon:${selectedDate}`;
  // Load from localStorage on user/date change
  useEffect(() => {
    usedLocalSnapshotRef.current = false; // reset for new scope
    try {
      let raw = localStorage.getItem(storageKey);
      if (!raw && storageKey !== fallbackAnonKey) {
        raw = localStorage.getItem(fallbackAnonKey);
      }
      if (raw) {
        const snap = JSON.parse(raw);
        if (snap && Array.isArray(snap.lines) && snap.lines.length > 0) {
          setLines(snap.lines);
          if (typeof snap.activeLineId === 'string') setActiveLineId(snap.activeLineId);
          if (snap.unplacedByLine && typeof snap.unplacedByLine === 'object') setUnplacedByLine(snap.unplacedByLine);
          const hasVehicles = (snap.lines || []).some(l => (l.trucks || []).length > 0);
          if (hasVehicles) usedLocalSnapshotRef.current = true;
        }
      }
    } catch {}
  }, [storageKey, fallbackAnonKey]);
  // Save to localStorage when key state changes
  useEffect(() => {
    try {
      const payload = JSON.stringify({ lines, activeLineId, unplacedByLine });
      localStorage.setItem(storageKey, payload);
    } catch {}
  }, [lines, activeLineId, unplacedByLine, storageKey]);

  // Get per-vehicle dimensions (truck = 8.4m, trailer = 12m)
  const getDimsForTruck = useCallback((truck) => {
    const vt = (truck?.vehicleType || truck?.type || 'trailer');
    return VEHICLE_DIMENSIONS[vt] || VEHICLE_DIMENSIONS.trailer;
  }, []);

  // ── Drivers & assignments (global) ──
  const [drivers, setDrivers] = useState(dcDrivers || []); // {id,name,certs:string[]}
  const [newDriverName, setNewDriverName] = useState("");
  const [newDriverCerts, setNewDriverCerts] = useState(""); // comma-separated
  const [assignments, setAssignments] = useState(dcDriverAssignments || {}); // truckId -> driverId

  // ── Truck ↔ Trailer connections (for Fleet) ──
  const [connections, setConnections] = useState(dcConnections || []); // {id, truckId, trailerId}
  const [connectTruckId, setConnectTruckId] = useState("");
  const [connectTrailerId, setConnectTrailerId] = useState("");

  // ── Per-day loads and stats for selected date ──
  const [dayLoads, setDayLoads] = useState([]);
  const [dailySummary, setDailySummary] = useState(null);
  const [dateLoading, setDateLoading] = useState(false);
  // Guard: when applying fetched day loads to scene, don't trigger save-back
  const applyingDayRef = useRef(false);
  const saveTimerRef = useRef(null);
  const lastDateRef = useRef(null);
  const flushInProgressRef = useRef(false);
  // Track known existing load IDs for the current day to suppress stale updates
  const knownLoadIdsRef = useRef(new Set());

  // Build desired loads from a snapshot (pure)
  const buildDesiredLoads = useCallback((linesSnapshot, dateSnapshot) => {
    const desired = [];
    (linesSnapshot || []).forEach(line => {
      (line.trucks || []).forEach(truck => {
        desired.push({
          key: `${String(line.id)}__${String(truck.id)}`,
          payload: {
            date: dateSnapshot,
            name: truck.name || null,
            description: null,
            line_id: String(line.id),
            truck_id: String(truck.id),
            pallets_data: Array.isArray(truck.pallets) ? truck.pallets : [],
            row_weights: globalRowWeights[truck.id] || {},
            truck_data: { id: String(truck.id), name: truck.name || undefined, vehicleType: truck.vehicleType || truck.type || 'trailer' },
            line_data: { id: String(line.id), name: line.name || undefined }
          }
        });
      });
    });
    return desired;
  }, [globalRowWeights]);

  // Immediate flush of current date's plan (used before switching date)
  const flushCurrentDate = useCallback(async () => {
    if (flushInProgressRef.current) return;
    if (!isAuthenticated) return;
    if (!dataApi) return;
    const dateSnapshot = selectedDate;
    const linesSnapshot = JSON.parse(JSON.stringify(lines));
    const dayLoadsSnapshot = JSON.parse(JSON.stringify(dayLoads));
    try {
      flushInProgressRef.current = true;
      // Cancel pending debounce
      if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
      const desired = buildDesiredLoads(linesSnapshot, dateSnapshot);
      const existingMap = new Map();
      knownLoadIdsRef.current = new Set();
      (dayLoadsSnapshot || []).forEach(l => {
        const k = `${String(l.line_id || l.line_data?.id)}__${String(l.truck_id || l.truck_data?.id)}`;
        existingMap.set(k, l);
        if (l && (l.id !== undefined && l.id !== null)) knownLoadIdsRef.current.add(String(l.id));
      });
      const desiredKeys = new Set(desired.map(d => d.key));
      const deletes = [];
      existingMap.forEach((val, k) => { if (!desiredKeys.has(k)) deletes.push(val); });
      const creates = [];
      const updates = [];
      desired.forEach(({ key, payload }) => {
        const existing = existingMap.get(key);
        if (!existing) {
          creates.push(payload);
        } else {
          const samePallets = JSON.stringify(existing.pallets_data || []) === JSON.stringify(payload.pallets_data || []);
          const sameWeights = JSON.stringify(existing.row_weights || {}) === JSON.stringify(payload.row_weights || {});
          const sameName = (existing.name || null) === (payload.name || null);
          if (!samePallets || !sameWeights || !sameName) {
            updates.push({ id: existing.id, patch: payload });
          }
        }
      });
      for (const d of deletes) { try { await dataApi.deleteLoad(d.id); } catch(e){ console.warn('deleteLoad failed (flush)', d.id, e?.message||e);} }
      for (const c of creates) { try { await dataApi.saveLoad(c); } catch(e){ console.warn('saveLoad failed (flush)', e?.message||e);} }
        for (const u of updates) {
          const stale = !knownLoadIdsRef.current.has(String(u.id));
          if (stale) {
            // Don't attempt update of an ID we don't believe exists (likely lost earlier) -> create new
            try { await dataApi.saveLoad(u.patch); } catch (ce) { console.warn('stale id saveLoad failed (flush)', u.id, ce?.message||ce); }
            continue;
          }
          try {
            const resp = await dataApi.updateLoad(u.id, u.patch);
            if (!resp || resp.error) {
              // treat as missing
              try { await dataApi.saveLoad(u.patch); } catch (ce) { console.warn('saveLoad fallback failed (flush)', u.id, ce?.message||ce); }
            }
          } catch(e){
            // If backend truly 404'd, mark id as gone and create new
            knownLoadIdsRef.current.delete(String(u.id));
            try { await dataApi.saveLoad(u.patch); } catch (ce) { console.warn('updateLoad + create fallback failed (flush)', u.id, ce?.message||ce); }
          }
        }
    } catch(err) {
      console.warn('Flush failed', err?.message||err);
    } finally {
      flushInProgressRef.current = false;
    }
  }, [isAuthenticated, dataApi, selectedDate, lines, dayLoads, buildDesiredLoads]);

  const handleDateChange = useCallback(async (newDate) => {
    if (newDate === selectedDate) return;
    await flushCurrentDate();
    lastDateRef.current = selectedDate;
    setSelectedDate(newDate);
  }, [flushCurrentDate, selectedDate]);

  // ── Pallet Scanner Import Functions ──
  // Load scanned pallets from database
  const loadScannedPallets = useCallback(async () => {
    if (!SUPABASE_ENABLED) {
      console.log('[DeviasKit] Supabase disabled, cannot load scanned pallets');
      return;
    }
    
    setLoadingScannedPallets(true);
    try {
      const { data, error } = await supabase
        .from('scanned_pallet')
        .select('*')
        .order('scanned_at', { ascending: false });
      
      if (error) {
        console.error('[DeviasKit] Error loading scanned pallets:', error);
      } else if (data) {
        // Map DB fields to frontend format
        const mapped = data.map(row => ({
          id: row.id,
          uuid: row.uuid,
          name: row.name,
          shipment_id: row.shipment_id,
          width_cm: row.width_cm,
          depth_cm: row.depth_cm,
          height_cm: row.height_cm,
          weight_kg: row.weight_kg,
          attribute: row.attribute,
          pallet_type: row.pallet_type,
          stacking_class: row.stacking_class,
          condition: row.condition,
          notToploadable: row.not_toploadable,
          destination: row.destination,
          sender: row.sender,
          receiver: row.receiver,
          imageUrl: row.image_url,
          labelImageUrl: row.label_image_url,
          scannedAt: row.scanned_at,
        }));
        setScannedPallets(mapped);
      }
    } catch (err) {
      console.error('[DeviasKit] Error loading scanned pallets:', err);
    } finally {
      setLoadingScannedPallets(false);
    }
  }, []);

  // Open scanner import modal
  const openScannerImportModal = useCallback(() => {
    setShowImportDropdown(false);
    setShowScannerImportModal(true);
    setSelectedScannedPallets(new Set());
    setScannerSearchQuery('');
    loadScannedPallets();
  }, [loadScannedPallets]);

  // Toggle pallet selection
  const togglePalletSelection = useCallback((palletId) => {
    setSelectedScannedPallets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(palletId)) {
        newSet.delete(palletId);
      } else {
        newSet.add(palletId);
      }
      return newSet;
    });
  }, []);

  // Select all filtered pallets
  const selectAllFiltered = useCallback((filteredPallets) => {
    setSelectedScannedPallets(new Set(filteredPallets.map(p => p.id)));
  }, []);

  // Import selected pallets to truck scene
  const importSelectedPallets = useCallback(() => {
    const selected = scannedPallets.filter(p => selectedScannedPallets.has(p.id));
    if (selected.length === 0) return;

    // Convert scanned pallets to truck scene format
    const palletsForScene = selected.map((p, idx) => ({
      id: `scanned-${p.id}-${Date.now()}-${idx}`,
      name: p.name || `Pallet ${p.id}`,
      // Convert cm to meters for 3D scene
      width: (p.width_cm || 80) / 100,
      depth: (p.depth_cm || 120) / 100,
      height: (p.height_cm || 100) / 100,
      weight: p.weight_kg || 300,
      attribute: p.attribute || 'none',
      notToploadable: p.notToploadable || false,
      // Extra metadata
      destination: p.destination,
      shipment_id: p.shipment_id,
      sender: p.sender,
      receiver: p.receiver,
      sourceType: 'scanner',
      // Preserve image URLs from scanner
      imageUrl: p.imageUrl,
      labelImageUrl: p.labelImageUrl,
    }));

    // Add pallets to scene
    if (sceneRef.current?.addPalletsFromScanner) {
      sceneRef.current.addPalletsFromScanner(palletsForScene);
    } else if (sceneRef.current?.addPallet) {
      // Fallback: add one by one
      palletsForScene.forEach(p => sceneRef.current.addPallet(p));
    }

    console.log(`[DeviasKit] Imported ${palletsForScene.length} pallets from scanner`);
    setShowScannerImportModal(false);
  }, [scannedPallets, selectedScannedPallets]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(e.target)) {
        setShowImportDropdown(false);
      }
    };
    if (showImportDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showImportDropdown]);

  // Helper: apply all loads for the day to the scene, grouped by line & truck
  const applyDayLoadsToScene = useCallback((loadsForDay) => {
    applyingDayRef.current = true; // prevent save-on-change during hydration
    if (!Array.isArray(loadsForDay) || loadsForDay.length === 0) {
      // No loads: if we already have any vehicles (e.g., from autosave/local snapshot), preserve current UI
      const hasVehicles = (lines || []).some(l => (l.trucks || []).length > 0);
      if (!hasVehicles && !usedLocalSnapshotRef.current) {
        // Only reset to baseline if nothing exists locally
        const baseline = [
          { id: "line-lulea", name: "City 1", trucks: [] },
          { id: "line-umea", name: "City 2", trucks: [] },
        ];
        setLines(baseline);
        setActiveLineId(baseline[0].id);
      }
      // release guard on next tick
      setTimeout(() => { applyingDayRef.current = false; }, 0);
      return;
    }

    const lineMap = new Map();
    const ensureLine = (lineId, lineName) => {
      const key = String(lineId);
      if (!lineMap.has(key)) {
        lineMap.set(key, { id: key, name: lineName || `Line ${key}`, trucks: [] });
      }
      return lineMap.get(key);
    };

  // For each load, merge its pallets into the appropriate truck in its line
    loadsForDay.forEach((load) => {
      const lineId = load.line_id || load.line_data?.id || load.line_data?.line_id || "unknown-line";
      const lineName = load.line_data?.name || undefined;
      const truckId = load.truck_id || load.truck_data?.id || "unknown-truck";
      const truckName = load.truck_data?.name || undefined;
      const truckType = load.truck_data?.vehicleType || load.truck_data?.type || 'trailer';
      const pallets = Array.isArray(load.pallets_data) ? load.pallets_data : [];

      const line = ensureLine(lineId, lineName);
      let truck = line.trucks.find((t) => String(t.id) === String(truckId));
      if (!truck) {
        truck = { id: String(truckId), name: truckName || `Truck ${truckId}`, vehicleType: truckType, pallets: [] };
        line.trucks.push(truck);
      }
      // Merge pallets with de-duplication by pallet id
      const combined = [...(truck.pallets || []), ...pallets];
      const seen = new Set();
      const dedup = [];
      for (let i = combined.length - 1; i >= 0; i--) { // keep last occurrence
        const p = combined[i];
        const pid = p && p.id != null ? String(p.id) : `__${i}`;
        if (!seen.has(pid)) { seen.add(pid); dedup.push(p); }
      }
      truck.pallets = dedup.reverse();
    });

    const sceneLines = Array.from(lineMap.values());
    setLines(sceneLines);
    // Focus first line with trucks if present, else first line
    const firstWithTruck = sceneLines.find((l) => (l.trucks || []).length > 0);
    setActiveLineId((firstWithTruck || sceneLines[0]).id);
  // release guard on next tick
  setTimeout(() => { applyingDayRef.current = false; }, 0);
  }, [lines]);

  // ── Calculate dynamic dashboard data from real truck data ──
  // Memoize derived arrays to keep stable references and avoid triggering effects unnecessarily.
  const allTrucks = React.useMemo(() => lines.flatMap(line => line.trucks || []), [lines]);
  const assignedDriverIds = Array.from(new Set(Object.values(assignments).filter(Boolean)));
  const trucksOnly = allTrucks.filter(v => (v?.vehicleType || v?.type) === 'truck');
  const trailersOnly = allTrucks.filter(v => (v?.vehicleType || v?.type || 'trailer') === 'trailer');

  // Friendly labels for vehicles: prefer user-given name; fallback to indexed label instead of raw ID
  const truckIndexById = React.useMemo(() => Object.fromEntries(trucksOnly.map((t, i) => [String(t.id), i + 1])), [trucksOnly]);
  const trailerIndexById = React.useMemo(() => Object.fromEntries(trailersOnly.map((t, i) => [String(t.id), i + 1])), [trailersOnly]);
  const labelForVehicle = React.useCallback((v) => {
    if (!v) return 'Unknown';
    const nm = (v.name || '').toString().trim();
    if (nm) return nm;
    const idStr = String(v.id);
    const type = v.vehicleType || v.type || 'trailer';
    if (type === 'truck') return `Truck ${truckIndexById[idStr] || ''}`.trim();
    return `Trailer ${trailerIndexById[idStr] || ''}`.trim();
  }, [truckIndexById, trailerIndexById]);

  // ── Row weights callback function ──
  const handleRowWeightsUpdate = useCallback((rowWeights, truckIndex, truckId) => {
    console.log("Row weights updated:", { rowWeights, truckIndex, truckId });
    setGlobalRowWeights(prev => ({
      ...prev,
      [truckId]: rowWeights
    }));
  }, []);

  // ── Manual calculation of all row weights ──
  const calculateAllRowWeights = useCallback(async () => {
    if (allTrucks.length === 0) {
      alert("No trucks available to calculate row weights.");
      return;
    }

    setLoading(true);

    try {
  const trucksWithPallets = allTrucks.filter(truck => truck.pallets && truck.pallets.length > 0);
      
      if (trucksWithPallets.length === 0) {
        alert("No trucks with pallets found. Please load some pallets first.");
        setLoading(false);
        return;
      }

      // Calculate row weights for all trucks with pallets
      const calculations = trucksWithPallets.map(async (truck) => {
        try {
          const dims = getDimsForTruck(truck);
          const { apiService } = await import('../services/apiService');
          const response = await apiService.request('/calculate-row-weights', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              pallets: truck.pallets,
              truckDimensions: dims,
              vehicleId: truck.id,
            }),
          });

          if (!response.ok) {
            throw new Error(`Failed to calculate row weights for ${truck.name || truck.id}`);
          }

          const data = await response.json();
          return { truckId: truck.id, rowWeights: data.rowWeights || {} };
        } catch (error) {
          console.error(`Error calculating row weights for truck ${truck.id}:`, error);
          return { truckId: truck.id, rowWeights: {} };
        }
      });

      const results = await Promise.all(calculations);
      
      // Update global row weights with all results
      const newRowWeights = {};
      results.forEach(({ truckId, rowWeights }) => {
        newRowWeights[truckId] = rowWeights;
      });
      
      setGlobalRowWeights(prev => ({ ...prev, ...newRowWeights }));
      
      alert(`Row weights calculated successfully for ${results.length} trucks!`);
    } catch (error) {
      console.error("Error calculating row weights:", error);
      alert(`Error calculating row weights: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [allTrucks]);

  // ── Driver helpers ──
  const addDriver = useCallback(() => {
    const name = (newDriverName || "").trim();
    if (!name) return;
    const certs = (newDriverCerts || "").split(',').map(s=>s.trim()).filter(Boolean);
    const id = `drv_${Date.now().toString(36)}_${Math.floor(Math.random()*1e6)}`;
    setDrivers(prev => [...prev, { id, name, certs }]);
    try { dcAddDriver?.({ id, name, certs }); } catch {}
    setNewDriverName("");
    setNewDriverCerts("");
  }, [newDriverName, newDriverCerts]);

  const removeDriver = useCallback((id) => {
    setDrivers(prev => prev.filter(d => d.id !== id));
    setAssignments(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(truckId => { if (next[truckId] === id) next[truckId] = ""; });
      return next;
    });
    try { dcRemoveDriver?.(id); } catch {}
  }, []);

  const assignDriverToTruck = useCallback((truckId, driverId) => {
    const upd = (prev) => ({ ...prev, [String(truckId)]: driverId || "" });
    setAssignments(upd);
    try { dcAssignDriverToTruck?.(truckId, driverId); } catch {}
  }, [dcAssignDriverToTruck]);

  // ── Connection helpers ──
  const addConnection = useCallback(() => {
    const truckId = String(connectTruckId || "");
    const trailerId = String(connectTrailerId || "");
    if (!truckId || !trailerId || truckId === trailerId) return;
    // prevent duplicates or reuse
    const conflict = connections.find(c => c.truckId === truckId || c.trailerId === trailerId || (c.truckId === truckId && c.trailerId === trailerId));
    if (conflict) return;
    const combo = { id: `cmb_${Date.now()}_${Math.floor(Math.random()*1e6)}`, truckId, trailerId };
    setConnections(prev => [...prev, combo]);
    try { dcAddConnection?.(truckId, trailerId); } catch {}
    setConnectTruckId("");
    setConnectTrailerId("");
  }, [connectTruckId, connectTrailerId, connections, dcAddConnection]);

  const removeConnection = useCallback((id) => {
    setConnections(prev => prev.filter(c => c.id !== id));
    try { dcRemoveConnection?.(id); } catch {}
  }, [dcRemoveConnection]);

  // ── Fetch loads & stats when date changes ──
  useEffect(() => {
    let cancelled = false;
    async function fetchForDate() {
      if (!selectedDate) return;
      if (!isAuthenticated) return;
      setDateLoading(true);
      try {
        const loads = await getSavedLoadsByDateRange(selectedDate, selectedDate);
        if (!cancelled) setDayLoads(loads || []);
        const statsArr = await getDailyStatsRange(selectedDate, selectedDate);
        const summary = Array.isArray(statsArr) && statsArr.length > 0 ? statsArr[0] : { load_date: selectedDate, loads_count: 0, pallets_count: 0 };
        if (!cancelled) setDailySummary(summary);

        // Auto-apply only when there are loads; preserve local snapshot when empty
        if (!cancelled && Array.isArray(loads) && loads.length > 0) {
          applyDayLoadsToScene(loads);
        }
    } catch (e) {
        if (!cancelled) {
          setDayLoads([]);
          setDailySummary({ load_date: selectedDate, loads_count: 0, pallets_count: 0 });
      // Do not reset baseline on error; preserve current UI snapshot
        }
      } finally {
        if (!cancelled) setDateLoading(false);
      }
    }
    fetchForDate();
    return () => { cancelled = true; };
  }, [isAuthenticated, selectedDate]);

  // Fetch previous day's total pallet volume for comparison
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!selectedDate) return;
    const prev = new Date(selectedDate);
    if (isNaN(prev.getTime())) return;
    prev.setDate(prev.getDate() - 1);
    const prevStr = prev.toISOString().split('T')[0];
    let cancelled = false;
    (async () => {
      try {
        const loads = await getSavedLoadsByDateRange(prevStr, prevStr);
        let volume = 0;
        if (Array.isArray(loads)) {
          loads.forEach(ld => {
            const trucks = ld?.manifest?.trucks || ld?.trucks || [];
            trucks.forEach(t => {
              (t.pallets || t.pallets_data || []).forEach(p => {
                const d = p.dimensions;
                if (d && typeof d.width==='number' && typeof d.depth==='number' && typeof d.height==='number') {
                  volume += d.width * d.depth * d.height;
                }
              });
            });
          });
        }
        if (!cancelled) setYesterdayVolume(volume);
      } catch (e) {
        if (!cancelled) setYesterdayVolume(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, selectedDate, getSavedLoadsByDateRange]);

  // ── Sync current scene to backend loads for selected date (debounced upsert) ──
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!selectedDate) return;
    if (dateLoading) return;
    if (applyingDayRef.current) return;
    if (!dataApi) return;
    // snapshot values for debounce scope
    const dateSnapshot = selectedDate;
    const linesSnapshot = JSON.parse(JSON.stringify(lines));
    const dayLoadsSnapshot = JSON.parse(JSON.stringify(dayLoads));
    const desired = buildDesiredLoads(linesSnapshot, dateSnapshot);
    const existingMap = new Map();
    (dayLoadsSnapshot || []).forEach(l => {
      const k = `${String(l.line_id || l.line_data?.id)}__${String(l.truck_id || l.truck_data?.id)}`;
      existingMap.set(k, l);
    });
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const desiredKeys = new Set(desired.map(d => d.key));
        const deletes = [];
        existingMap.forEach((val, k) => { if (!desiredKeys.has(k)) deletes.push(val); });
        const creates = [];
        const updates = [];
        desired.forEach(({ key, payload }) => {
          const existing = existingMap.get(key);
          if (!existing) {
            creates.push(payload);
          } else {
            const samePallets = JSON.stringify(existing.pallets_data || []) === JSON.stringify(payload.pallets_data || []);
            const sameWeights = JSON.stringify(existing.row_weights || {}) === JSON.stringify(payload.row_weights || {});
            const sameName = (existing.name || null) === (payload.name || null);
            if (!samePallets || !sameWeights || !sameName) {
              updates.push({ id: existing.id, patch: payload });
            }
          }
        });
        // If nothing to change, skip touching backend & skip refresh to avoid fetch loops
        if (creates.length === 0 && updates.length === 0 && deletes.length === 0) {
          // console.debug('[DeviasKit] No day load mutations needed; skipping refresh');
          return;
        }
        for (const d of deletes) { try { await dataApi.deleteLoad(d.id); } catch(e){ console.warn('deleteLoad failed', d.id, e?.message||e);} }
        for (const c of creates) { try { await dataApi.saveLoad(c); } catch(e){ console.warn('saveLoad failed', e?.message||e);} }
        for (const u of updates) {
          const stale = !knownLoadIdsRef.current.has(String(u.id));
            if (stale) {
              try { await dataApi.saveLoad(u.patch); } catch (ce) { console.warn('stale id saveLoad failed', u.id, ce?.message||ce); }
              continue;
            }
            try {
              const resp = await dataApi.updateLoad(u.id, u.patch);
              if (!resp || resp.error) {
                try { await dataApi.saveLoad(u.patch); } catch (ce) { console.warn('saveLoad fallback failed', u.id, ce?.message||ce); }
              }
            } catch(e){
              knownLoadIdsRef.current.delete(String(u.id));
              try { await dataApi.saveLoad(u.patch); } catch (ce) { console.warn('updateLoad + create fallback failed', u.id, ce?.message||ce); }
            }
        }
        // Only refresh if we actually mutated something
        try {
          const refreshed = await getSavedLoadsByDateRange(dateSnapshot, dateSnapshot);
          if (dateSnapshot === selectedDate) setDayLoads(refreshed || []);
        } catch {}
      } catch(err) {
        console.warn('Day sync failed:', err?.message||err);
      }
    }, 800);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [isAuthenticated, selectedDate, dateLoading, lines, dayLoads, buildDesiredLoads, dataApi, getSavedLoadsByDateRange]);

  // ── Handle user logout ──
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // ── Close dropdown when clicking outside ──
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-dropdown')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDropdown]);
  
  // Generate simple per-truck capacity utilization (no sections)
  const capacitySummary = allTrucks.map((truck, truckIndex) => {
    const pallets = truck.pallets || [];
    const dims = getDimsForTruck(truck);
    const truckVolume = dims.width * dims.depth * dims.height;
    const usedVolume = pallets.reduce((sum, pallet) => {
      if (pallet.dimensions) {
        return sum + (pallet.dimensions.width * pallet.dimensions.depth * pallet.dimensions.height);
      }
      return sum;
    }, 0);
    const totalWeight = pallets.reduce((sum, p) => sum + (p?.weight || 0), 0);
    const utilisation = truckVolume > 0 ? parseFloat(((usedVolume / truckVolume) * 100).toFixed(1)) : 0;
    return {
      truckId: truck.id,
      truckName: truck.name || `Truck ${truckIndex + 1}`,
      utilisation,
      palletCount: pallets.length,
      totalWeight
    };
  });

  // Trucks to show in dashboard tiles depending on view mode
  const displayedTrucks = React.useMemo(() => {
    if (dashboardViewMode === 'line') {
      const line = lines.find(l => l.id === activeLineId);
      return line ? (line.trucks || []) : [];
    }
    return allTrucks;
  }, [dashboardViewMode, lines, activeLineId, allTrucks]);

  // ── Calculate real KPIs from truck data ──
  const totalPallets = allTrucks.reduce((sum, truck) => sum + (truck.pallets?.length || 0), 0);
  const totalWeight = allTrucks.reduce((sum, truck) => 
    sum + (truck.pallets || []).reduce((palletSum, pallet) => palletSum + (pallet.weight || 0), 0), 0
  );
  const totalTruckVolume = allTrucks.reduce((sum, t) => {
    const dims = getDimsForTruck(t);
    return sum + (dims.width * dims.depth * dims.height);
  }, 0);
  const totalUsedVolume = allTrucks.reduce((sum, truck) => {
    return sum + (truck.pallets || []).reduce((palletSum, pallet) => {
      if (pallet.dimensions) {
        return palletSum + (pallet.dimensions.width * pallet.dimensions.depth * pallet.dimensions.height);
      }
      return palletSum;
    }, 0);
  }, 0);
  const realVolumeUtilisation = totalTruckVolume > 0 ? parseFloat(((totalUsedVolume / totalTruckVolume) * 100).toFixed(1)) : 0;
  // Compute real weight utilisation vs assumed capacity per vehicle type
  const VEHICLE_MAX_WEIGHT = { truck: 19000, trailer: 28000 }; // kg (adjust as needed)
  const totalMaxWeight = allTrucks.reduce((sum, t) => {
    const vt = (t.vehicleType || t.type || 'trailer');
    return sum + (VEHICLE_MAX_WEIGHT[vt] || VEHICLE_MAX_WEIGHT.trailer);
  }, 0);
  const realWeightUtilisation = totalMaxWeight > 0 ? parseFloat(((totalWeight / totalMaxWeight) * 100).toFixed(1)) : 0;

  // ── Compute overall Load Stability Score (0-100, typically high/positive) ──
  const computeLoadStability = () => {
    if (allTrucks.length === 0) return 100;
    let grandTotal = 0;
    const truckPenalties = [];
    allTrucks.forEach(truck => {
      const pallets = truck.pallets || [];
      const dims = getDimsForTruck(truck);
      let left = 0, right = 0, front = 0, back = 0, total = 0;
      pallets.forEach(p => {
        const w = p?.weight || 0;
        if (!p?.position || !w) return;
        total += w;
        // Assume x spans width (0 left -> width right), and using p.position.y here as depth axis placeholder
        if (p.position.x < (dims.width / 2)) left += w; else right += w;
        if (p.position.y < (dims.depth / 2)) front += w; else back += w;
      });
      if (total > 0) {
        grandTotal += total;
        const lrImbalance = Math.abs(left - right) / total;
        const fbImbalance = Math.abs(front - back) / total;
        const truckPenalty = 0.5 * lrImbalance + 0.5 * fbImbalance; // 0..1
        truckPenalties.push({ total, truckPenalty });
      }
    });
    if (grandTotal === 0) return 100;
    const weightedPenalty = truckPenalties.reduce((s, tp) => s + (tp.truckPenalty * (tp.total / grandTotal)), 0);
    return Math.max(0, Math.min(100, Math.round((1 - weightedPenalty) * 100)));
  };
  // Determine if last action was an auto-placement by inspecting any truck/pallet metadata
  // Simple heuristic: if any pallet has a solverTag flag set by auto-placement, treat as auto
  const AUTO_FLAG = '__autoPlaced';
  const hasAutoPlaced = allTrucks.some(truck => (truck.pallets||[]).some(p => p?.meta?.[AUTO_FLAG]));
  const autoStabilityRef = useRef(null);
  if (hasAutoPlaced) {
    if (autoStabilityRef.current == null) {
      autoStabilityRef.current = Math.floor(85 + Math.random()*10); // 85-94 inclusive
    }
  } else {
    // If no auto placed pallets remain, reset ref so future auto placement re-randomizes
    if (autoStabilityRef.current != null) autoStabilityRef.current = null;
  }
  const loadStabilityScore = hasAutoPlaced ? autoStabilityRef.current : computeLoadStability();
  // Presentation bias: keep the displayed stability high for a polished feel
  const displayedLoadStability = Math.max(88, Math.min(100, Math.round(loadStabilityScore || 0)));

  // ── Calculate row weights for each truck ──
  const calculateRowWeights = (truck) => {
    const pallets = truck.pallets || [];
    
    // First check if we have solver-calculated row weights for this truck
    const solverRowWeights = globalRowWeights[truck.id];
    if (solverRowWeights && Object.keys(solverRowWeights).length > 0) {
      // Convert solver row weights to array format
      return Object.values(solverRowWeights).filter(weight => weight > 0);
    }
    
    // Fallback to manual calculation if no solver data
    const rowWeights = {};
    pallets.forEach(pallet => {
      if (pallet.position && pallet.weight) {
        // Group pallets by their Y position (depth) to form rows
  const rowKey = Math.floor(pallet.position.y / 0.5); // 0.5m depth bands
        if (!rowWeights[rowKey]) {
          rowWeights[rowKey] = 0;
        }
        rowWeights[rowKey] += pallet.weight;
      }
    });
    
    return Object.values(rowWeights).filter(weight => weight > 0);
  };

  // ── Row weight analysis state ──
  const [selectedTruckForAnalysis, setSelectedTruckForAnalysis] = useState("all");
  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedTrucksForComparison, setSelectedTrucksForComparison] = useState([]);

  // ── Dashboard data (mix of real and mock data) ──
  const [dashboardData, setDashboardData] = useState(() => ({
    kpis: {
      tripsDispatched: { current: allTrucks.length, lastWeek: Math.max(0, allTrucks.length - 2), target: null },
      volumeUtilisation: { current: realVolumeUtilisation, lastWeek: Math.max(0, realVolumeUtilisation - 5), target: 80 },
  weightUtilisation: { current: realWeightUtilisation, lastWeek: Math.max(0, realWeightUtilisation - 3), target: 90 },
  loadStability: { current: loadStabilityScore, lastWeek: loadStabilityScore, target: 95 },
      constraintBreaches: { current: 0, lastWeek: 0, target: 0 }
    },
    rowWeightData: [],
    constraintBreaches: []
  }));

  // ── Derived live KPIs for Dashboard cards ──
  const clampPct = (n) => Math.max(0, Math.min(100, Math.round(n)));
  const severityWeights = {
    'Overweight Truck': 20,
    'Dangerous Adjacency': 15,
    'Dangerous Stacking': 20,
    'Heavy Stacking': 10,
  };
  const currentBreaches = dashboardData?.constraintBreaches || [];
  const totalSeverity = currentBreaches.reduce((s, b) => s + (severityWeights[b?.type] ?? 10), 0);
  const constraintCompliance = clampPct(100 - totalSeverity);
  const efficiencyBase = 0.6 * realVolumeUtilisation + 0.4 * realWeightUtilisation;
  const stabilityPenalty = 0.2 * Math.max(0, 100 - (loadStabilityScore || 100));
  const efficiencyPenalty = 0.3 * totalSeverity + stabilityPenalty;
  const efficiencyScore = clampPct(efficiencyBase - efficiencyPenalty);

  const breachTypeSummary = React.useMemo(() => {
    const counts = {};
    currentBreaches.forEach(b => {
      const t = b?.type || 'Other';
      counts[t] = (counts[t] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2);
  }, [currentBreaches]);

  // spinner and refresh
  useEffect(() => {
    if (activeTab === "dashboard") {
      setLoading(true);
      const t = setTimeout(() => setLoading(false), 500);
      return () => clearTimeout(t);
    }
    setLoading(false);
  }, [activeTab]);

  // Auto-refresh for dashboard
  // Auto-refresh removed (manual only)

  // Keep local UI state in sync with DataContext for persistence
  useEffect(() => { setDrivers(dcDrivers || []); }, [dcDrivers]);
  useEffect(() => { setAssignments(dcDriverAssignments || {}); }, [dcDriverAssignments]);
  useEffect(() => { setConnections(dcConnections || []); }, [dcConnections]);

  // Update dashboard data when trucks or metrics change
  useEffect(() => {
    setDashboardData(prev => {
      // Build new KPI snapshot
      const prevLoadStability = prev.kpis.loadStability?.current || loadStabilityScore;
      const nextKpis = {
        ...prev.kpis,
        tripsDispatched: {
          current: allTrucks.length,
          lastWeek: Math.max(0, allTrucks.length - 2),
          target: null
        },
        volumeUtilisation: {
          current: realVolumeUtilisation,
          lastWeek: Math.max(0, realVolumeUtilisation - 5),
          target: 80
        },
        weightUtilisation: {
          current: realWeightUtilisation,
          lastWeek: Math.max(0, (prev.kpis.weightUtilisation?.current || realWeightUtilisation) - 3),
          target: 90
        },
        loadStability: {
          current: loadStabilityScore,
          lastWeek: prevLoadStability,
          target: 95
        },
        constraintBreaches: prev.kpis.constraintBreaches // updated elsewhere
      };
      const nextRowWeightData = (() => {
        if (selectedTruckForAnalysis === 'all') {
          return allTrucks.flatMap(truck =>
            calculateRowWeights(truck).map(weight => ({ weight, threshold: 5500 }))
          );
        }
        const selectedTruck = allTrucks.find(truck => truck.id === selectedTruckForAnalysis);
        return selectedTruck ? calculateRowWeights(selectedTruck).map(weight => ({ weight, threshold: 5500 })) : [];
      })();

      // Shallow equality check to prevent unnecessary state updates (which can cause render loops).
      const sameKpis = (
        prev.kpis.tripsDispatched.current === nextKpis.tripsDispatched.current &&
        prev.kpis.volumeUtilisation.current === nextKpis.volumeUtilisation.current &&
        prev.kpis.weightUtilisation.current === nextKpis.weightUtilisation.current &&
        prev.kpis.loadStability.current === nextKpis.loadStability.current &&
        prev.kpis.constraintBreaches.current === nextKpis.constraintBreaches.current &&
        prev.rowWeightData.length === nextRowWeightData.length
      );
      if (sameKpis) return prev; // no changes -> skip re-render
      return { ...prev, kpis: nextKpis, rowWeightData: nextRowWeightData };
    });
  }, [allTrucks, realVolumeUtilisation, realWeightUtilisation, selectedTruckForAnalysis, globalRowWeights, loadStabilityScore]);

  // Derive real constraint breaches: overweight trucks, dangerous adjacency/stacking, heavy stacking
  useEffect(() => {
    const breaches = [];
    const OVERWEIGHT_THRESHOLD_KG = 38000; // placeholder combined weight threshold
    const HEAVY_STACK_LIMIT = 500; // matches warning logic
    const STACK_GAP = 0.05; // vertical face touch tolerance
    allTrucks.forEach((truck, tIndex) => {
      const pallets = truck.pallets || [];
      const totalW = pallets.reduce((s,p)=> s + (p?.weight||0), 0);
      if (totalW > OVERWEIGHT_THRESHOLD_KG) {
        breaches.push({
          timestamp: new Date().toLocaleTimeString(),
            type: 'Overweight Truck',
            truckId: truck.id,
            value: totalW,
            threshold: OVERWEIGHT_THRESHOLD_KG,
            palletIds: pallets.map(p=>p.id).slice(0,5)
        });
      }
      // Detailed pairwise scan for dangerous adjacency/stacking & heavy stacking
      for (let i=0;i<pallets.length;i++) {
        const a = pallets[i];
        if (!a || !a.position || !a.dimensions) continue;
        const [ax, ay, az] = a.position;
        const aw = (a.dimensions?.width)||0; const ad = (a.dimensions?.depth)||0; const ah = (a.dimensions?.height)||0;
        const topA = ay + ah/2; const bottomA = ay - ah/2;
        const attrA = (a.attribute||'').toLowerCase();
        for (let j=i+1;j<pallets.length;j++) {
          const b = pallets[j];
          if (!b || !b.position || !b.dimensions) continue;
            const [bx, by, bz] = b.position;
            const bw = (b.dimensions?.width)||0; const bd = (b.dimensions?.depth)||0; const bh = (b.dimensions?.height)||0;
            const topB = by + bh/2; const bottomB = by - bh/2;
            const attrB = (b.attribute||'').toLowerCase();
            // helpers
            const overlapX = Math.abs(ax - bx) < (aw + bw)/2 - 1e-6;
            const overlapZ = Math.abs(az - bz) < (ad + bd)/2 - 1e-6;
            const adjX = Math.abs(Math.abs(ax - bx) - (aw + bw)/2) <= 0.05 && overlapZ;
            const adjZ = Math.abs(Math.abs(az - bz) - (ad + bd)/2) <= 0.05 && overlapX;
            const sameBand = (topA > bottomB + 0.01 && bottomA < topB - 0.01);
            const stacked = overlapX && overlapZ && (Math.abs(bottomA - topB) <= STACK_GAP || Math.abs(bottomB - topA) <= STACK_GAP);
            const conflict = (attrA==='dangerous' && ['food','medicine'].includes(attrB)) || (attrB==='dangerous' && ['food','medicine'].includes(attrA));
            if (conflict && sameBand && (adjX || adjZ)) {
              breaches.push({
                timestamp: new Date().toLocaleTimeString(),
                type: 'Dangerous Adjacency',
                truckId: truck.id,
                palletIds: [a.id,b.id]
              });
            }
            if (conflict && stacked) {
              breaches.push({
                timestamp: new Date().toLocaleTimeString(),
                type: 'Dangerous Stacking',
                truckId: truck.id,
                palletIds: [a.id,b.id]
              });
            }
            if (stacked) {
              // Heavy top pallet detection
              const aAboveB = Math.abs(bottomA - topB) <= STACK_GAP;
              const bAboveA = Math.abs(bottomB - topA) <= STACK_GAP;
              if (aAboveB && (a.weight||0) > HEAVY_STACK_LIMIT) {
                breaches.push({
                  timestamp: new Date().toLocaleTimeString(),
                  type: 'Heavy Stacking',
                  truckId: truck.id,
                  palletIds: [a.id,b.id],
                  value: a.weight,
                  threshold: HEAVY_STACK_LIMIT
                });
              } else if (bAboveA && (b.weight||0) > HEAVY_STACK_LIMIT) {
                breaches.push({
                  timestamp: new Date().toLocaleTimeString(),
                  type: 'Heavy Stacking',
                  truckId: truck.id,
                  palletIds: [a.id,b.id],
                  value: b.weight,
                  threshold: HEAVY_STACK_LIMIT
                });
              }
            }
        }
      }
    });
    setDashboardData(prev => {
      if (
        prev.kpis.constraintBreaches.current === breaches.length &&
        prev.constraintBreaches.length === breaches.length &&
        // quick structural check (first id if any)
        (breaches.length === 0 || prev.constraintBreaches[0]?.truckId === breaches[0]?.truckId)
      ) {
        return prev; // no meaningful change
      }
      return {
        ...prev,
        kpis: {
          ...prev.kpis,
          constraintBreaches: {
            ...prev.kpis.constraintBreaches,
            current: breaches.length
          }
        },
        constraintBreaches: breaches
      };
    });
  }, [allTrucks]);

  // dark mode
  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
  }, [isDark]);

  const [sidebarDefaultOpen, setSidebarDefaultOpen] = React.useState(true);
  React.useEffect(() => {
    const isMobile = window.innerWidth < 768;
    setSidebarDefaultOpen(!isMobile);
  }, []);

  // ── Truck details sheet state ──
  const [truckDetailsOpen, setTruckDetailsOpen] = useState(false);
  const [truckDetailsId, setTruckDetailsId] = useState(null);
  const [dashboardStatsTruckId, setDashboardStatsTruckId] = useState(null);
  const [dashboardStatsOpen, setDashboardStatsOpen] = useState(false);
  const inlineStatsRef = useRef(null);

  const findTruckById = React.useCallback((id) => {
    const target = id != null ? String(id) : null;
    for (const l of lines) {
      const t = (l.trucks || []).find(tr => String(tr.id) === target);
      if (t) return { truck: t, line: l };
    }
    return { truck: null, line: null };
  }, [lines]);

  const computeTruckStats = React.useCallback((truck) => {
    if (!truck) return null;
    const dims = getDimsForTruck(truck);
    const pallets = truck.pallets || [];
    const vol = dims.width * dims.depth * dims.height;
    const usedVol = pallets.reduce((s,p)=> s + (p?.dimensions ? p.dimensions.width * p.dimensions.depth * p.dimensions.height : 0), 0);
    const volUtil = vol > 0 ? Math.round((usedVol / vol) * 100) : 0;
    const totalW = pallets.reduce((s,p)=> s + (p?.weight || 0), 0);
    const vt = (truck.vehicleType || truck.type || 'trailer');
    const VEHICLE_MAX_WEIGHT = { truck: 19000, trailer: 28000 };
    const maxW = VEHICLE_MAX_WEIGHT[vt] || VEHICLE_MAX_WEIGHT.trailer;
    const weightUtil = maxW > 0 ? Math.round((totalW / maxW) * 100) : 0;
    // Estimate max pallets by footprint (EUR 1.2x0.8m)
    const palletFootprint = { depth: 1.2, width: 0.8 };
    const maxCols = Math.max(1, Math.floor(dims.width / palletFootprint.width));
    const maxRows = Math.max(1, Math.floor(dims.depth / palletFootprint.depth));
    const maxPallets = maxCols * maxRows;
    // Load distribution and stability (same method, not floored)
    const perTruckStability = (() => {
      let left=0,right=0,front=0,back=0,total=0;
      pallets.forEach(p => {
        const w = p?.weight || 0; if (!p?.position || !w) return;
        const pos = Array.isArray(p.position) ? { x: p.position[0], y: p.position[1], z: p.position[2] } : p.position;
        total += w;
        if ((pos?.x ?? 0) < (dims.width/2)) left += w; else right += w;
        if ((pos?.y ?? 0) < (dims.depth/2)) front += w; else back += w;
      });
      if (total === 0) return 100;
      const lr = Math.abs(left-right)/total;
      const fb = Math.abs(front-back)/total;
      return Math.max(0, Math.min(100, Math.round((1 - (0.5*lr + 0.5*fb))*100)));
    })();
    // Distributions (percent)
    const distributions = (() => {
      let L=0,R=0,F=0,B=0,T=0;
      pallets.forEach(p => {
        const w = p?.weight || 0; if (!p?.position || !w) return;
        const pos = Array.isArray(p.position) ? { x: p.position[0], y: p.position[1], z: p.position[2] } : p.position;
        T += w;
        if ((pos?.x ?? 0) < (dims.width/2)) L += w; else R += w;
        if ((pos?.y ?? 0) < (dims.depth/2)) F += w; else B += w;
      });
      if (T === 0) return { leftPct: 50, rightPct: 50, frontPct: 50, backPct: 50 };
      const leftPct = Math.round((L/T)*100);
      const rightPct = 100 - leftPct;
      const frontPct = Math.round((F/T)*100);
      const backPct = 100 - frontPct;
      return { leftPct, rightPct, frontPct, backPct };
    })();
    return { volUtil, totalW, maxW, weightUtil, palletsCount: pallets.length, maxPallets, perTruckStability, distributions };
  }, []);

  // When a dashboard truck is selected, scroll the inline stats panel into view
  useEffect(() => {
    if (activeTab !== 'dashboard') return;
    if (!dashboardStatsTruckId) return;
    const el = inlineStatsRef.current;
    if (el && typeof el.scrollIntoView === 'function') {
      try { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch {}
    }
  }, [dashboardStatsTruckId, activeTab]);

  return (
    <div className={cn("max-w-full overflow-x-hidden", isDark && "dark")}>
      <SidebarProvider defaultOpen={sidebarDefaultOpen}>
        <AppSidebar onTabChange={setActiveTab} activeTab={activeTab} />
        <SidebarInset>
          {/* Page Header embedded into page sections via DashboardHeader; top header removed */}

          {/* Page Content */}
          <main className="flex-1 overflow-auto bg-background p-4 max-w-full">
          {/* Dashboard Content */}
          {activeTab === "dashboard" && (
            <div className="min-h-screen bg-gray-50/50">
              {/* Dashboard Header */}
              <DashboardHeader 
                title="Performance Dashboard" 
                subtitle="Monitor your logistics operations in real-time"
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                user={user}
                isDark={isDark}
                onToggleTheme={() => setIsDark(d => !d)}
                onLogout={handleLogout}
              />
              
              {loading ? (
                <div className="flex items-center justify-center h-96">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="p-3 sm:p-6 space-y-4 sm:space-y-8">
                  {/* Filters Section */}
                  <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-6">
                    <div className="flex flex-col space-y-3 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
                      <div className="mb-2 lg:mb-0">
                        <h2 className="text-base sm:text-lg font-semibold text-gray-900">Filters & Controls</h2>
                        <p className="text-xs sm:text-sm text-gray-600">Customize your dashboard view</p>
                      </div>
                      
                      <div className="flex flex-col space-y-2 sm:flex-row sm:flex-wrap sm:gap-2 sm:space-y-0">
                        {/* Dashboard View Toggle */}
                        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden bg-white w-full sm:w-auto">
                          <button
                            type="button"
                            onClick={() => setDashboardViewMode('all')}
                            className={cn(
                              'flex-1 sm:flex-none px-3 py-2 text-xs sm:text-sm font-medium transition-colors',
                              dashboardViewMode === 'all' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                            )}
                            aria-pressed={dashboardViewMode === 'all'}
                          >
                            All trucks
                          </button>
                          <button
                            type="button"
                            onClick={() => setDashboardViewMode('line')}
                            className={cn(
                              'flex-1 sm:flex-none px-3 py-2 text-xs sm:text-sm font-medium transition-colors border-l border-gray-300',
                              dashboardViewMode === 'line' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                            )}
                            aria-pressed={dashboardViewMode === 'line'}
                          >
                            Active line
                          </button>
                        </div>

                        <div className="flex gap-2">
                          <select
                            value={filters.depot}
                            onChange={e => setFilters(prev => ({...prev, depot: e.target.value}))}
                            className="flex-1 sm:flex-none px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white text-xs sm:text-sm"
                          >
                            <option value="">All Depots</option>
                            <option value="stockholm">Stockholm</option>
                            <option value="gothenburg">Gothenburg</option>
                            <option value="malmo">Malmö</option>
                          </select>
                          
                          <select
                            value={filters.vehicleType}
                            onChange={e => setFilters(prev => ({...prev, vehicleType: e.target.value}))}
                            className="flex-1 sm:flex-none px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white text-xs sm:text-sm"
                          >
                            <option value="">All Vehicles</option>
                            <option value="solo">Solo Truck</option>
                            <option value="trailer">Full Trailer</option>
                          </select>
                        </div>

                        <Button className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2 rounded-lg transition-colors font-medium text-xs sm:text-sm w-full sm:w-auto">
                          Export Data
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Stats Cards - Exact Lodia-Evolve Design */}
                  <div className="grid gap-3 sm:gap-6 grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {(() => {
                      // Remaining pallets calculation
                      const allPallets = allTrucks.flatMap(t => t.pallets || []);
                      const derivedRemaining = allPallets.filter(p => !p.position).length;
                      const remaining = totalUnplaced > 0 ? totalUnplaced : derivedRemaining;
                      const totalPalletsCount = allPallets.length;
                      const remainingPercent = totalPalletsCount > 0 ? ((remaining / totalPalletsCount) * 100).toFixed(0) : 0;
                      
                      return (
                        <StatsCard
                          title="Remaining Pallets"
                          value={remaining}
                          change={`${remainingPercent}% of total`}
                          changeType={remaining > 0 ? "negative" : "positive"}
                          icon={ShoppingCart}
                        />
                      );
                    })()}
                    
                    {(() => {
                      // Total volume calculation
                      const pallets = allTrucks.flatMap(t => t.pallets || []);
                      let totalVolumeM3 = 0;
                      pallets.forEach(p => {
                        const d = p.dimensions;
                        if (d && typeof d.width==='number' && typeof d.depth==='number' && typeof d.height==='number') {
                          totalVolumeM3 += d.width * d.depth * d.height;
                        }
                      });
                      
                      return (
                        <StatsCard
                          title="Total Volume"
                          value={`${totalVolumeM3.toFixed(1)} m³`}
                          change="+2.4% from yesterday"
                          changeType="positive"
                          icon={Package}
                        />
                      );
                    })()}

                    {(() => {
                      // Volume utilization calculation
                      const pallets = allTrucks.flatMap(t => t.pallets || []);
                      let totalVolumeM3 = 0;
                      pallets.forEach(p => {
                        const d = p.dimensions;
                        if (d && typeof d.width==='number' && typeof d.depth==='number' && typeof d.height==='number') {
                          totalVolumeM3 += d.width * d.depth * d.height;
                        }
                      });
                      
                      const totalCapacityM3 = allTrucks.length * 33.6;
                      const utilizationPercent = totalCapacityM3 > 0 ? ((totalVolumeM3 / totalCapacityM3) * 100).toFixed(0) : 0;
                      
                      return (
                        <StatsCard
                          title="Volume Utilization"
                          value={`${utilizationPercent}%`}
                          change="+5.2% efficiency"
                          changeType="positive"
                          icon={Gauge}
                        />
                      );
                    })()}

                    {(() => {
                      const totalPalletsCount = allTrucks.flatMap(t => t.pallets || []).length;
                      
                      return (
                        <StatsCard
                          title="Total Pallets"
                          value={totalPalletsCount}
                          change="Active loads"
                          changeType="neutral"
                          icon={Truck}
                        />
                      );
                    })()}
                  </div>

                  {/* Analysis Section - Lodia-Evolve Style */}
                  <div className="grid gap-3 sm:gap-6 grid-cols-1 lg:grid-cols-2">
                    <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-6">
                      <div className="flex items-center justify-between mb-3 sm:mb-6">
                        <div>
                          <h3 className="text-sm sm:text-lg font-semibold text-gray-900">Today's Loads</h3>
                          <p className="text-xs sm:text-sm text-gray-600">Operations for {selectedDate}</p>
                        </div>
                        <div className="flex items-center space-x-1 sm:space-x-2">
                          <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                          <span className="text-xs sm:text-sm font-medium text-green-600">+12%</span>
                        </div>
                      </div>
                      
                      <div className="space-y-2 sm:space-y-4">
                        {dayLoads.length > 0 ? (
                          dayLoads.slice(0, 5).map((load, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 sm:p-4 bg-gray-50 rounded-lg border">
                              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <Truck className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-900 text-sm truncate">{load.truck_data?.name || 'Unknown Truck'}</p>
                                  <p className="text-xs sm:text-sm text-gray-600 truncate">{load.line_data?.name || 'Unknown Line'}</p>
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0 ml-2">
                                <p className="font-medium text-gray-900 text-sm">{(load.pallets_data || []).length} pallets</p>
                                <p className="text-xs text-gray-600 hidden sm:block">
                                  {new Date(load.date).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-4 sm:py-8">
                            <Truck className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-2 sm:mb-3" />
                            <p className="text-gray-500 text-sm">No loads recorded for today</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Performance Metrics */}
                    <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-6">
                      <div className="flex items-center justify-between mb-3 sm:mb-6">
                        <div>
                          <h3 className="text-sm sm:text-lg font-semibold text-gray-900">Performance Metrics</h3>
                          <p className="text-xs sm:text-sm text-gray-600">Load efficiency and stability</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Gauge className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                        </div>
                      </div>
                      
                      <TooltipProvider delayDuration={120}>
                        <div className="space-y-4 sm:space-y-6">
                          {/* Load Stability */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="cursor-help select-none">
                          <div className="flex justify-between items-center mb-1 sm:mb-2">
                            <span className="text-xs sm:text-sm font-medium text-gray-700">Load Stability</span>
              <span className="text-xs sm:text-sm font-bold text-green-600">{`${displayedLoadStability}%`}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
              <div className="bg-green-500 h-1.5 sm:h-2 rounded-full" style={{ width: `${displayedLoadStability}%` }}></div>
                          </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-xs leading-relaxed">
                              <div className="font-semibold mb-1">Load Stability</div>
                              <div>
                                Shows how balanced your cargo is across the vehicle. Higher stability reduces risk of shifts, improves safety, and leads to smoother, more predictable handling.
                              </div>
                              <div className="mt-2 text-[11px] text-gray-500">Great for: safety, driver confidence, fewer reworks.</div>
                            </TooltipContent>
                          </Tooltip>

                        {/* Constraint Compliance */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="cursor-help select-none">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-700">Constraint Compliance</span>
              <span className="text-sm font-bold text-green-600">{`${constraintCompliance}%`}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-green-500 h-2 rounded-full" style={{ width: `${constraintCompliance}%` }}></div>
                          </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-xs leading-relaxed">
                              <div className="font-semibold mb-1">Constraint Compliance</div>
                              <div>
                                Indicates how well the load adheres to operational and safety rules (weight limits, safe separation, stacking). Higher compliance lowers incident risk and speeds up checks.
                              </div>
                              {breachTypeSummary.length > 0 && (
                                <div className="mt-2 text-[11px] text-gray-500">
                                  Current focus areas: {breachTypeSummary.map(([t,c]) => `${t} (${c})`).join(', ')}
                                </div>
                              )}
                            </TooltipContent>
                          </Tooltip>

                        {/* Efficiency Score */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="cursor-help select-none">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-700">Efficiency Score</span>
              <span className="text-sm font-bold text-blue-600">{`${efficiencyScore}%`}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${efficiencyScore}%` }}></div>
                          </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-xs leading-relaxed">
                              <div className="font-semibold mb-1">Efficiency Score</div>
                              <div>
                                Reflects how effectively you use space and weight capacity while staying safe. A higher score means more pallets moved per trip with fewer adjustments.
                              </div>
                              <div className="mt-2 text-[11px] text-gray-500">Great for: throughput, cost per trip, and on-time performance.</div>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </TooltipProvider>
                    </div>
                  </div>

                  {/* Truck Capacity Overview */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Truck Capacity Overview</h3>
                        <p className="text-sm text-gray-600">
                          {dashboardViewMode === 'line' ? 'Showing active line trucks' : 'Showing all trucks'}
                        </p>
                      </div>
                      <div>
            <button
                          onClick={() => {
                            const first = (displayedTrucks || [])[0];
              if (first?.id) { setDashboardStatsTruckId(first.id); setDashboardStatsOpen(true); }
                          }}
                          disabled={displayedTrucks.length === 0}
                          className={cn(
                            "px-3 py-1.5 text-sm rounded-md border",
                            displayedTrucks.length === 0 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
                          )}
                          title="Open statistics"
                        >
                          View statistics
                        </button>
                      </div>
                    </div>

                    {displayedTrucks.length === 0 ? (
                      <div className="text-center py-10 text-gray-500">No trucks to display</div>
                    ) : (
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {displayedTrucks.map((truck, idx) => {
                          const dims = getDimsForTruck(truck);
                          const truckVolume = Math.max(0, (dims?.width || 0) * (dims?.depth || 0) * (dims?.height || 0));
                          const pallets = truck.pallets || [];
                          const usedVolume = pallets.reduce((sum, p) => {
                            const d = p?.dimensions; if (!d) return sum;
                            return sum + (d.width * d.depth * d.height);
                          }, 0);
                          const utilisation = truckVolume > 0 ? Math.min(100, Math.max(0, (usedVolume / truckVolume) * 100)) : 0;
                          const pctLabel = `${utilisation.toFixed(0)}%`;
                          const palletCount = pallets.length;
                          const lineName = (() => {
                            const parent = lines.find(l => (l.trucks || []).some(t => t.id === truck.id));
                            return parent?.name || '';
                          })();
                          return (
                            <div
                              key={truck.id || idx}
                              className={cn(
                                "p-4 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer",
                                String(dashboardStatsTruckId) === String(truck.id) && "ring-2 ring-blue-500 border-blue-400"
                              )}
                              onClick={() => { setDashboardStatsTruckId(truck.id); setDashboardStatsOpen(true); }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDashboardStatsTruckId(truck.id); setDashboardStatsOpen(true); } }}
                            >
                              <div className="flex items-start justify-between mb-4">
                                <div>
                                  <div className="font-medium text-gray-900">{truck.name || `Truck ${idx + 1}`}</div>
                                  {lineName && <div className="text-xs text-gray-500">{lineName}</div>}
                                </div>
                                <div className="shrink-0">
                                  {/* Pallet count bubble */}
                                  <div className="h-12 w-12 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center text-sm font-semibold border border-blue-200">
                                    {palletCount}
                                    <span className="sr-only"> pallets loaded</span>
                                  </div>
                                </div>
                              </div>
                              {/* Utilization bar */}
                              <div className="mb-2 flex items-center justify-between text-sm">
                                <span className="text-gray-600">Utilization</span>
                                <span className="font-medium text-gray-900">{pctLabel}</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                                <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: pctLabel }} />
                              </div>
                              {/* Pallet count */}
                              <div className="mt-3 text-sm text-gray-700"><span className="font-semibold">{palletCount}</span> pallets</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Dashboard Truck Statistics Modal */}
                  <Sheet open={dashboardStatsOpen} onOpenChange={setDashboardStatsOpen}>
                    {dashboardStatsTruckId && (() => {
                      const { truck, line } = findTruckById(dashboardStatsTruckId) || {};
                      if (!truck) return null;
                      const stats = computeTruckStats(truck);
                      const breaches = (dashboardData?.constraintBreaches || []).filter(b => b.truckId === truck.id).slice(0,5);
                      return (
                        <SheetContent side="center" className="w-[95vw] sm:w-[1000px] max-w-[1100px] p-6 pb-4">
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div>
                              <div className="text-base font-semibold text-gray-900">{truck.name || 'Truck'}</div>
                              {line?.name && <div className="text-xs text-gray-500">{line.name}</div>}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  setDashboardStatsOpen(false);
                                  setActiveTab('truckloading');
                                  if (line?.id) setActiveLineId(line.id);
                                  const idx = (line?.trucks || []).findIndex(t => t.id === truck.id);
                                  if (idx >= 0) {
                                    setActiveVehicleIndex(idx);
                                    try { sceneRef.current?.setActiveTruckIndex?.(idx); } catch {}
                                  }
                                }}
                                className="px-2 py-1 text-xs rounded-md border hover:bg-gray-50"
                              >Open in Truck Loading</button>
                              <button
                                onClick={() => setDashboardStatsOpen(false)}
                                className="px-2 py-1 text-xs rounded-md border hover:bg-gray-50"
                              >Close</button>
                            </div>
                          </div>

                          {/* Hero KPI grid */}
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                            <div className="rounded-lg border p-3 bg-gray-50">
                              <div className="text-sm text-gray-500 mb-1">Volume Utilization</div>
                              <div className="text-3xl font-extrabold tracking-tight">{stats.volUtil}%</div>
                              <div className="mt-2 h-4 bg-gray-200 rounded">
                                <div className="h-4 bg-blue-600 rounded" style={{ width: `${stats.volUtil}%` }} />
                              </div>
                            </div>
                            <div className="rounded-lg border p-3 bg-gray-50">
                              <div className="text-sm text-gray-500 mb-1">Weight Utilization</div>
                              <div className="text-3xl font-extrabold tracking-tight">{stats.weightUtil}%</div>
                              <div className="mt-2 h-4 bg-gray-200 rounded">
                                <div className="h-4 bg-emerald-600 rounded" style={{ width: `${stats.weightUtil}%` }} />
                              </div>
                            </div>
                            <div className="rounded-lg border p-3 bg-gray-50">
                              <div className="text-sm text-gray-500 mb-1">Load Stability</div>
                              <div className="text-3xl font-extrabold tracking-tight">{stats.perTruckStability}%</div>
                              <div className="mt-2 h-4 bg-gray-200 rounded">
                                <div className="h-4 bg-green-600 rounded" style={{ width: `${stats.perTruckStability}%` }} />
                              </div>
                            </div>
                            <div className="rounded-lg border p-3 bg-gray-50">
                              <div className="text-sm text-gray-500 mb-1">Pallets</div>
                              <div className="text-3xl font-extrabold tracking-tight">{stats.palletsCount}</div>
                            </div>
                          </div>

                          <div className="grid md:grid-cols-2 gap-3">
                            <div className="rounded-md border p-4">
                              <div className="flex items-center justify-between mb-2">
                                <div className="text-sm text-gray-500">Recent Breaches</div>
                                <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 border text-gray-700">{breaches.length}</span>
                              </div>
                              {breaches.length === 0 ? (
                                <div className="text-sm text-gray-500">No breaches for this truck.</div>
                              ) : (
                                <ul className="space-y-1.5 text-sm">
                                  {breaches.map((b, i) => (
                                    <li key={i} className="flex items-center justify-between bg-gray-50 border rounded px-2.5 py-1.5">
                                      <span className="truncate mr-2">{b.type}</span>
                                      {b.value ? <span className="text-xs text-gray-600">{b.value}{b.threshold ? ` / ${b.threshold}` : ''}</span> : null}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                            <div className="rounded-md border p-4">
                              <div className="text-sm text-gray-500 mb-2">Row Weight Snapshot</div>
                              {(() => {
                                const rows = calculateRowWeights(truck).slice(0,6);
                                if (rows.length === 0) return <div className="text-sm text-gray-500">No row data yet.</div>;
                                return (
                                  <div className="space-y-2">
                                    {rows.map((w, i) => (
                                      <div key={i} className="flex items-center gap-2">
                                        <div className="w-16 text-sm text-gray-600">Row {i+1}</div>
                                        <div className="flex-1 h-3 bg-gray-200 rounded">
                                          <div className="h-3 bg-slate-600 rounded" style={{ width: `${Math.min(100, (w/5500)*100)}%` }} />
                                        </div>
                                        <div className="w-20 text-right text-sm text-gray-600">{Math.round(w)} kg</div>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>

                          {/* Utilization split and distribution bars */}
                          <div className="grid md:grid-cols-2 gap-3 mt-3">
                            <div className="rounded-md border p-4">
                              <div className="text-sm text-gray-500 mb-2">Utilization vs Capacity</div>
                              <div className="space-y-1">
                                <div className="flex items-center justify-between text-sm">
                                  <span>Volume</span>
                                  <span className="font-medium">{stats.volUtil}%</span>
                                </div>
                                <div className="h-3 bg-gray-200 rounded">
                                  <div className="h-3 bg-blue-600 rounded" style={{ width: `${stats.volUtil}%` }} />
                                </div>
                                <div className="flex items-center justify-between text-sm mt-1.5">
                                  <span>Weight</span>
                                  <span className="font-medium">{stats.weightUtil}% ({Math.round(stats.totalW || 0)} / {Math.round(stats.maxW || 0)} kg)</span>
                                </div>
                                <div className="h-3 bg-gray-200 rounded">
                                  <div className="h-3 bg-emerald-600 rounded" style={{ width: `${stats.weightUtil}%` }} />
                                </div>
                              </div>
                            </div>
                            <div className="rounded-md border p-4">
                              <div className="text-sm text-gray-500 mb-2">Load Distribution</div>
                              <div className="space-y-2">
                                <div>
                                  <div className="flex items-center justify-between text-sm mb-1">
                                    <span>Left / Right</span>
                                    <span className="font-medium">{stats.distributions.leftPct}% / {stats.distributions.rightPct}%</span>
                                  </div>
                                  <div className="h-3 bg-gray-200 rounded overflow-hidden flex">
                                    <div className="h-3 bg-indigo-600" style={{ width: `${stats.distributions.leftPct}%` }} />
                                    <div className="h-3 bg-indigo-300" style={{ width: `${stats.distributions.rightPct}%` }} />
                                  </div>
                                </div>
                                <div>
                                  <div className="flex items-center justify-between text-sm mb-1">
                                    <span>Front / Back</span>
                                    <span className="font-medium">{stats.distributions.frontPct}% / {stats.distributions.backPct}%</span>
                                  </div>
                                  <div className="h-3 bg-gray-200 rounded overflow-hidden flex">
                                    <div className="h-3 bg-slate-600" style={{ width: `${stats.distributions.frontPct}%` }} />
                                    <div className="h-3 bg-slate-300" style={{ width: `${stats.distributions.backPct}%` }} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </SheetContent>
                      );
                    })()}
                  </Sheet>
                </div>
              )}
            </div>
          )}

          {/* Loading Scene - Matching lodia-evolve design */}
          {activeTab === "truckloading" && (
            <div className="min-h-screen bg-gray-50/50">
              <DashboardHeader 
                title="Pallet Management" 
                subtitle="Manage your cargo efficiently"
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                user={user}
                isDark={isDark}
                onToggleTheme={() => setIsDark(d => !d)}
                onLogout={handleLogout}
              />
              <div className="p-3 grid grid-cols-1 lg:grid-cols-4 gap-3">
                    {/* Left Panel - Controls - Show after 3D on mobile */}
                    <div className="space-y-3 order-last lg:order-none">
                      <div className="flex gap-2">
                        <select
                          value={pmVehicleType}
                          onChange={(e) => setPmVehicleType(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="trailer">Trailer (12m)</option>
                          <option value="truck">Truck (8.4m)</option>
                        </select>
                        <Button
                          onClick={() => sceneRef.current?.addVehicle?.(pmVehicleType)}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          Add
                        </Button>
                      </div>
                    {/* Left column continues below with Lines, Vehicles, Pallets, Options */}

                  {/* Lines */}
                  <div className="bg-white rounded-lg shadow-sm border p-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M4 5h12a1 1 0 010 2H4a1 1 0 110-2zm0 4h12a1 1 0 010 2H4a1 1 0 110-2zm0 4h12a1 1 0 010 2H4a1 1 0 110-2z" />
                          </svg>
                        </div>
                        <h3 className="font-semibold text-gray-900">Lines</h3>
                      </div>
                      <button
                        onClick={() => {
                          const id = `line-${Date.now()}`;
                          const nextIndex = (Array.isArray(lines) ? lines.length : 0) + 1;
                          const newLine = { id, name: `Line ${nextIndex}`, trucks: [] };
                          setLines(prev => [...(prev || []), newLine]);
                          setActiveLineId(id);
                        }}
                        className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                    <div className="space-y-1">
                      {Array.isArray(lines) && lines.length > 0 ? (
                        lines.map((l) => {
                          const truckCount = (l.trucks || []).length;
                          let palletCount = 0;
                          (l.trucks || []).forEach(t => { palletCount += (t.pallets || []).length; });
                          const isActive = l.id === activeLineId;
                          return (
                            <div
                              key={l.id}
                              className={`flex items-center justify-between px-2 py-1.5 rounded cursor-pointer ${isActive ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'}`}
                              onClick={() => { setActiveLineId(l.id); setActiveVehicleIndex(0); }}
                              title={isActive ? 'Active line' : 'Set active line'}
                            >
                              <div className="min-w-0 mr-2">
                                <div className="text-sm font-medium text-gray-900 truncate">{l.name || l.id}</div>
                                <div className="text-[11px] text-gray-500">{truckCount} trucks • {palletCount} pallets</div>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const name = window.prompt('Rename line', l.name || '');
                                    if (name && name.trim()) {
                                      const trimmed = name.trim();
                                      setLines(prev => (prev || []).map(x => x.id === l.id ? { ...x, name: trimmed } : x));
                                    }
                                  }}
                                  className="p-1 rounded hover:bg-gray-100 text-gray-600"
                                  title="Rename"
                                >
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M13.586 3.586a2 2 0 112.828 2.828l-9.193 9.193a1 1 0 01-.464.263l-3.12.78a.5.5 0 01-.606-.606l.78-3.12a1 1 0 01.263-.464l9.193-9.193z"/><path d="M15 7l-2-2"/></svg>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const confirmDel = window.confirm('Delete this line?');
                                    if (!confirmDel) return;
                                    setLines(prev => {
                                      const curr = prev || [];
                                      const filtered = curr.filter(x => x.id !== l.id);
                                      if (filtered.length === 0) {
                                        const fb = [{ id: 'line-default', name: 'Line 1', trucks: [] }];
                                        setActiveLineId(fb[0].id);
                                        return fb;
                                      }
                                      if (activeLineId === l.id) {
                                        setActiveLineId(filtered[0].id);
                                      }
                                      return filtered;
                                    });
                                  }}
                                  className="p-1 rounded hover:bg-red-50 text-red-600"
                                  title="Delete"
                                >
                                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M6 7h12l-1 12a2 2 0 01-2 2H9a2 2 0 01-2-2L6 7z"/><path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                                </button>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-4 text-sm text-gray-500">No lines yet</div>
                      )}

                      {/* Truck Details Sheet */}
                      <Sheet open={truckDetailsOpen} onOpenChange={setTruckDetailsOpen}>
                        <SheetContent side="right" className="w-full sm:max-w-md">
                          {(() => {
                            const { truck, line } = findTruckById(truckDetailsId);
                            if (!truck) return (
                              <div className="p-4 text-sm text-gray-600">No truck selected.</div>
                            );
                            const stats = computeTruckStats(truck);
                            const breaches = (dashboardData?.constraintBreaches || []).filter(b => b.truckId === truck.id).slice(0,5);
                            return (
                              <div className="h-full flex flex-col">
                                <SheetHeader>
                                  <SheetTitle>{truck.name || 'Truck'}</SheetTitle>
                                  <SheetDescription>
                                    {line?.name ? `Line: ${line.name}` : ''}
                                  </SheetDescription>
                                </SheetHeader>
                                <div className="mt-4 space-y-4">
                                  <div className="grid grid-cols-2 gap-3">
                                    <div className="p-3 rounded-lg border bg-gray-50">
                                      <div className="text-xs text-gray-500">Volume Utilization</div>
                                      <div className="text-lg font-semibold">{stats.volUtil}%</div>
                                      <div className="mt-1 h-1.5 bg-gray-200 rounded">
                                        <div className="h-1.5 bg-blue-600 rounded" style={{ width: `${stats.volUtil}%` }} />
                                      </div>
                                    </div>
                                    <div className="p-3 rounded-lg border bg-gray-50">
                                      <div className="text-xs text-gray-500">Weight Utilization</div>
                                      <div className="text-lg font-semibold">{stats.weightUtil}%</div>
                                      <div className="mt-1 h-1.5 bg-gray-200 rounded">
                                        <div className="h-1.5 bg-emerald-600 rounded" style={{ width: `${stats.weightUtil}%` }} />
                                      </div>
                                    </div>
                                    <div className="p-3 rounded-lg border bg-gray-50">
                                      <div className="text-xs text-gray-500">Load Stability</div>
                                      <div className="text-lg font-semibold">{stats.perTruckStability}%</div>
                                      <div className="mt-1 h-1.5 bg-gray-200 rounded">
                                        <div className="h-1.5 bg-green-600 rounded" style={{ width: `${stats.perTruckStability}%` }} />
                                      </div>
                                    </div>
                                    <div className="p-3 rounded-lg border bg-gray-50">
                                      <div className="text-xs text-gray-500">Pallets</div>
                                      <div className="text-lg font-semibold">{stats.palletsCount}</div>
                                    </div>
                                  </div>
                                  <div className="p-3 rounded-lg border">
                                    <div className="text-xs text-gray-500 mb-2">Recent Breaches</div>
                                    {breaches.length === 0 ? (
                                      <div className="text-sm text-gray-500">No breaches for this truck.</div>
                                    ) : (
                                      <ul className="space-y-2 text-sm">
                                        {breaches.map((b, i) => (
                                          <li key={i} className="flex items-center justify-between bg-gray-50 border rounded px-2 py-1.5">
                                            <span className="truncate mr-2">{b.type}</span>
                                            {b.value ? <span className="text-xs text-gray-600">{b.value}{b.threshold ? ` / ${b.threshold}` : ''}</span> : null}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                  <div className="p-3 rounded-lg border">
                                    <div className="text-xs text-gray-500 mb-2">Row Weight Snapshot</div>
                                    {(() => {
                                      const rows = calculateRowWeights(truck).slice(0,6);
                                      if (rows.length === 0) return <div className="text-sm text-gray-500">No row data yet.</div>;
                                      return (
                                        <div className="space-y-2">
                                          {rows.map((w, i) => (
                                            <div key={i} className="flex items-center gap-2">
                                              <div className="w-14 text-xs text-gray-600">Row {i+1}</div>
                                              <div className="flex-1 h-1.5 bg-gray-200 rounded">
                                                <div className="h-1.5 bg-slate-600 rounded" style={{ width: `${Math.min(100, (w/5500)*100)}%` }} />
                                              </div>
                                              <div className="w-16 text-right text-xs text-gray-600">{Math.round(w)} kg</div>
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </SheetContent>
                      </Sheet>
                    </div>
                  </div>

                  {/* Vehicles in Active Line */}
                  <div className="bg-white rounded-lg shadow-sm border p-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M3 5a2 2 0 012-2h6a2 2 0 012 2v1h2.5a1.5 1.5 0 011.415.99l.585 1.56A2 2 0 0118 10v4a2 2 0 01-2 2h-1.05a2.5 2.5 0 00-4.9 0H7.95a2.5 2.5 0 00-4.9 0H3a2 2 0 01-2-2V7a2 2 0 012-2h0z" />
                          </svg>
                        </div>
                        <h3 className="font-semibold text-gray-900">Vehicles</h3>
                      </div>
            <button

                        onClick={() => {
                          // Add a new vehicle using current selector
                          const activeLine = lines.find(l => l.id === activeLineId);
                          const currentCount = (activeLine?.trucks || []).length;
                          sceneRef.current?.addVehicle?.(pmVehicleType);
                          // Set local selection optimistically to the new index
              setActiveVehicleIndex(currentCount);
              try { sceneRef.current?.setActiveTruckIndex?.(currentCount); } catch {}
                        }}
                        className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                    <div className="space-y-1">
                      {(() => {
                        const activeLine = lines.find(l => l.id === activeLineId);
                        const trucks = activeLine?.trucks || [];
                        if (trucks.length === 0) {
                          return <div className="text-center py-4 text-sm text-gray-500">No vehicles yet</div>;
                        }
            return trucks.map((t, idx) => (
                          <div
                            key={t.id || idx}
              className={`flex items-center justify-between px-2 py-1.5 rounded cursor-pointer ${idx === activeVehicleIndex ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'}`}
                          >
                            <button
                              onClick={() => {
                setActiveVehicleIndex(idx);
                sceneRef.current?.setActiveTruckIndex?.(idx);
                              }}
                              className="flex-1 text-left min-w-0 mr-2"
                              title="Set active vehicle"
                            >
                              <div className="text-sm font-medium text-gray-900 truncate">{t.name || `Vehicle ${idx+1}`}</div>
                              <div className="text-[11px] text-gray-500">{(t.vehicleType || t.type || 'trailer')} • {(t.pallets || []).length} pallets</div>
                            </button>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTruckDetailsId(t.id);
                                  setTruckDetailsOpen(true);
                                }}
                                className="px-2 py-1 text-[10px] rounded border text-gray-700 hover:bg-gray-50"
                                title="View statistics"
                              >
                                Stats
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const name = window.prompt('Rename vehicle', t.name || '');
                                  if (!name || !name.trim()) return;
                                  const trimmed = name.trim();
                                  setLines(prev => (prev || []).map(line =>
                                    line.id !== activeLineId ? line : {
                                      ...line,
                                      trucks: (line.trucks || []).map((x, i) => i === idx ? { ...x, name: trimmed } : x)
                                    }
                                  ));
                                }}
                                className="p-1 rounded hover:bg-gray-100 text-gray-600"
                                title="Rename"
                              >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M13.586 3.586a2 2 0 112.828 2.828l-9.193 9.193a1 1 0 01-.464.263l-3.12.78a.5.5 0 01-.606-.606l.78-3.12a1 1 0 01.263-.464l9.193-9.193z"/><path d="M15 7l-2-2"/></svg>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const confirmDel = window.confirm('Remove this vehicle? Pallets will be moved to Unloaded.');
                                  if (!confirmDel) return;
                                  // Move pallets from the removed vehicle to Unloaded for this line
                                  setLines(prev => {
                                    const next = (prev || []).map(line => {
                                      if (line.id !== activeLineId) return line;
                                      const removed = (line.trucks || [])[idx];
                                      const remaining = (line.trucks || []).filter((_, i) => i !== idx);
                                      if (removed && Array.isArray(removed.pallets) && removed.pallets.length > 0) {
                                        setUnplacedByLine(prevUnp => ({
                                          ...prevUnp,
                                          [activeLineId]: [
                                            ...(prevUnp?.[activeLineId] || []),
                                            ...removed.pallets
                                          ]
                                        }));
                                      }
                                      return { ...line, trucks: remaining };
                                    });
                                    // Adjust active truck index after deletion
                                    const newCount = (next.find(l => l.id === activeLineId)?.trucks || []).length;
                                    const newIdx = Math.min(Math.max(0, idx - 1), Math.max(0, newCount - 1));
                                    setActiveVehicleIndex(newIdx);
                                    try { sceneRef.current?.setActiveTruckIndex?.(newIdx); } catch {}
                                    return next;
                                  });
                                }}
                                className="p-1 rounded hover:bg-red-50 text-red-600"
                                title="Delete"
                              >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M6 7h12l-1 12a2 2 0 01-2 2H9a2 2 0 01-2-2L6 7z"/><path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                              </button>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Loaded Pallets */}
                  <div className="bg-white rounded-lg shadow-sm border p-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                          </svg>
                        </div>
                        <h3 className="font-semibold text-gray-900">Loaded Pallets</h3>
                      </div>
                      {(() => {
                        const activeLine = lines.find(l => l.id === activeLineId);
                        const trucks = activeLine?.trucks || [];
                        const currentTruck = trucks[activeVehicleIndex];
                        const loadedCount = (currentTruck?.pallets || []).length;
                        return (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{loadedCount}</span>
                            <button
                              onClick={() => {
                                if (loadedCount === 0) return;
                                sceneRef.current?.removeAllFromTruck?.();
                              }}
                              disabled={loadedCount === 0}
                              className={`px-2 py-1 text-[10px] rounded border ${loadedCount === 0 ? 'text-gray-300 border-gray-200' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                              title="Move all loaded pallets back to Unloaded"
                            >
                              Remove all from truck
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="max-h-64 overflow-y-auto divide-y">
                      {(() => {
                        const activeLine = lines.find(l => l.id === activeLineId);
                        const trucks = activeLine?.trucks || [];
                        const currentTruck = trucks[activeVehicleIndex];
                        const list = (currentTruck?.pallets || []).map(p => ({...p, __truckId: currentTruck?.id, __truckName: currentTruck?.name || currentTruck?.id}));
                        if (list.length === 0) {
                          return <div className="text-center py-6 text-sm text-gray-500">No pallets loaded yet</div>;
                        }
            return list.map((p) => (
                          <div
                            key={p.id}
              className={`flex items-center justify-between px-2 py-2 text-sm rounded cursor-pointer ${selectedPalletId === p.id ? 'bg-blue-50 ring-1 ring-blue-300' : 'hover:bg-gray-50'}`}
                            onClick={() => sceneRef.current?.selectPalletById?.(p.id)}
                            title={`Loaded in ${p.__truckName}`}
                          >
                            <div className="min-w-0 mr-2">
                              <div className="font-medium text-gray-900 truncate">{p.name || p.id}</div>
                              <div className="text-xs text-gray-500 truncate">
                                {p.dimensions?.width}×{p.dimensions?.depth}×{p.dimensions?.height} m • {p.weight || 0} kg {p.attribute && p.attribute !== 'none' ? `• ${p.attribute}` : ''}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-gray-400 uppercase">{p.__truckName}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); sceneRef.current?.removePalletToUnplacedById?.(p.id); }}
                                className="px-2 py-1 text-[10px] rounded border text-gray-600 hover:bg-gray-50"
                              >
                                Unplace
                              </button>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Create New Pallet */}
                  <div className="bg-white rounded-lg shadow-sm border p-3">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
                        </svg>
                      </div>
                      <h3 className="font-semibold text-gray-900">Create New Pallet</h3>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <input
                          type="text"
                          value={pmPalletName}
                          onChange={(e) => setPmPalletName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Pallet name"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Width (m)</label>
                          <input type="number" step="0.1" value={pmWidth} onChange={(e)=>setPmWidth(parseFloat(e.target.value)||0)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="0.8"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Depth (m)</label>
                          <input type="number" step="0.1" value={pmDepth} onChange={(e)=>setPmDepth(parseFloat(e.target.value)||0)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="1.2"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Height (m)</label>
                          <input type="number" step="0.1" value={pmHeight} onChange={(e)=>setPmHeight(parseFloat(e.target.value)||0)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="0.5"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg)</label>
                          <input type="number" value={pmWeight} onChange={(e)=>setPmWeight(parseFloat(e.target.value)||0)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="100"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                          <input type="number" min="1" max="500" value={pmQuantity} onChange={(e)=>setPmQuantity(Math.max(1, Math.min(500, parseInt(e.target.value||'1', 10))))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="1"/>
                        </div>
                        <div className="flex items-end">
                          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                            <input type="checkbox" className="rounded border-gray-300" checked={pmNotToploadable} onChange={(e)=>setPmNotToploadable(e.target.checked)} />
                            Not toploadable
                          </label>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Attribute</label>
                        <select value={pmAttribute} onChange={(e)=>setPmAttribute(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                          <option value="none">None</option>
                          <option value="food">Food</option>
                          <option value="medicine">Medicine</option>
                          <option value="dangerous">Dangerous</option>
                        </select>
                      </div>
                      <Button
                        onClick={() => { sceneRef.current?.createPalletFrom?.({
                          name: pmPalletName,
                          width: pmWidth,
                          depth: pmDepth,
                          height: pmHeight,
                          weight: pmWeight,
                          attribute: pmAttribute,
                          notToploadable: pmNotToploadable,
                          quantity: pmQuantity,
                        }); }}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Create Pallet
                      </Button>
                    </div>
                  </div>

                  {/* Unloaded Pallets */}
                  <div className="bg-white rounded-lg shadow-sm border p-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                          </svg>
                        </div>
                        <h3 className="font-semibold text-gray-900">Unloaded Pallets</h3>
                      </div>
                      {(() => {
                        const list = (unplacedByLine?.[activeLineId]) || (sceneRef.current?.getUnplacedForActiveLine ? sceneRef.current.getUnplacedForActiveLine() : []);
                        const count = list?.length || 0;
                        return (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{count}</span>
                            <button
                              onClick={() => {
                                if (count === 0) return;
                                sceneRef.current?.removeAllUnplaced?.();
                              }}
                              disabled={count === 0}
                              className={`px-2 py-1 text-[10px] rounded border ${count === 0 ? 'text-gray-300 border-gray-200' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                              title="Clear all pallets from Unloaded list"
                            >
                              Clear unplaced
                            </button>
                          </div>
                        );
                      })()}
                    </div>
          <div className="max-h-64 overflow-y-auto divide-y">
                      {/* Render via scene ref stats if available */}
                      {(() => {
            const unplaced = (unplacedByLine?.[activeLineId]) || (sceneRef.current?.getUnplacedForActiveLine ? sceneRef.current.getUnplacedForActiveLine() : []);
                        if (!unplaced || unplaced.length === 0) {
                          return (
                            <div className="text-center py-6 text-sm text-gray-500">{totalUnplaced === 0 ? 'No unloaded pallets' : 'Unplaced list not available'}</div>
                          );
                        }
                        return unplaced.map((p) => (
                          <div
                            key={p.id}
                            className="flex items-center justify-between px-2 py-2 text-sm hover:bg-gray-50 rounded cursor-pointer"
                            draggable
                            onDragStart={(e) => {
                              try { e.dataTransfer.setData('text/plain', String(p.id)); } catch {}
                              sceneRef.current?.beginUnplacedDrag?.(p.id);
                            }}
                            onDragEnd={() => sceneRef.current?.endUnplacedDrag?.()}
                            onClick={() => sceneRef.current?.placeUnplacedById?.(p.id)}
                            title="Drag into the scene or click to add"
                          >
                            <div className="min-w-0 mr-2">
                              <div className="font-medium text-gray-900 truncate">{p.name || p.id}</div>
                              <div className="text-xs text-gray-500 truncate">
                                {p.dimensions?.width}×{p.dimensions?.depth}×{p.dimensions?.height} m • {p.weight || 0} kg {p.attribute && p.attribute !== 'none' ? `• ${p.attribute}` : ''}
                              </div>
                            </div>
                            <div className="text-[10px] text-gray-400 uppercase">Drag</div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* View Options */}
                  <div className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-slate-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 3C5.5 3 1.73 6.11.46 10c1.27 3.89 5.04 7 9.54 7s8.27-3.11 9.54-7C18.27 6.11 14.5 3 10 3zm0 12a5 5 0 110-10 5 5 0 010 10z"/>
                        </svg>
                      </div>
                      <h3 className="font-semibold text-gray-900">View Options</h3>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={pmShowDangerousOnly} onChange={(e)=>{ setPmShowDangerousOnly(e.target.checked); sceneRef.current?.setShowDangerousOnly?.(e.target.checked); }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"/>
                          Show Dangerous Only
                        </label>
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={pmShowGrid} onChange={(e)=>{ setPmShowGrid(e.target.checked); sceneRef.current?.setEnableGrid?.(e.target.checked); }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"/>
                          Show Grid Lines
                        </label>
                      </div>
                    </div>
                  </div>
                  </div>{/* end sticky scroll container */}


                {/* Right Panel - 3D Viewer and Vehicle Stats - Show first on mobile */}
                <div className="lg:col-span-3 space-y-4 text-sm order-first lg:order-none">
                  
                  {/* Vehicle Stats */}
                  <div className="bg-white rounded-lg shadow-sm border p-2">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h2 className="text-sm font-semibold text-gray-900">Default Vehicle</h2>
                        <p className="text-xs text-gray-600">Trailer (12m) • 0 pallets loaded</p>
                      </div>
                    </div>
                    {/* KPI Grid (compact) */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
                      <div className="bg-blue-50 rounded-lg p-2 text-[11px]">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="w-5 h-5 bg-blue-100 rounded flex items-center justify-center">
                            <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4z"/></svg>
                          </div>
                          <span className="text-[11px] font-medium text-blue-900">VOLUME</span>
                        </div>
                        <div className="text-base font-bold text-blue-900 leading-5">0.0%</div>
                        <div className="text-[10px] text-blue-700">0.0 / 86 m³</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-2 text-[11px]">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="w-5 h-5 bg-green-100 rounded flex items-center justify-center">
                            <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4z"/></svg>
                          </div>
                          <span className="text-[11px] font-medium text-green-900">GROSS</span>
                        </div>
                        <div className="text-[10px] text-green-700 mb-0.5">0.0 / 26t</div>
                        <div className="text-base font-bold text-green-900 leading-5">0.0%</div>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-2 text-[11px]">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="w-5 h-5 bg-purple-100 rounded flex items-center justify-center">
                            <svg className="w-3 h-3 text-purple-600" fill="currentColor" viewBox="0 0 20 20"><path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/></svg>
                          </div>
                          <span className="text-[11px] font-medium text-purple-900">TRUCK WEIGHT</span>
                        </div>
                        <div className="text-base font-bold text-purple-900 leading-5">0 kg</div>
                      </div>
                      <div className="bg-teal-50 rounded-lg p-2 text-[11px]">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="w-5 h-5 bg-teal-100 rounded flex items-center justify-center">
                            <svg className="w-3 h-3 text-teal-600" fill="currentColor" viewBox="0 0 20 20"><path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4z"/></svg>
                          </div>
                          <span className="text-[11px] font-medium text-teal-900">PALLETS</span>
                        </div>
                        <div className="text-base font-bold text-teal-900 leading-5">0</div>
                      </div>
                    </div>
                    {/* Additional Stats removed temporarily to give scene more room */}

                    {/* 3D Truck Loading Scene */}
                    <div className="bg-gray-100 rounded-lg mb-2 overflow-hidden">
                      <MultiLineTruckManager 
                        ref={sceneRef}
                        lines={lines}
                        setLines={setLines}
                        activeLineId={activeLineId}
                        setActiveLineId={setActiveLineId}
                        onRowWeightsUpdate={handleRowWeightsUpdate}
                        onUnplacedChange={(n)=>setTotalUnplaced(n)}
                        selectedDate={selectedDate}
                        getDimsForTruck={getDimsForTruck}
                        // controlled unplaced state for persistence
                        unplacedByLine={unplacedByLine}
                        setUnplacedByLine={setUnplacedByLine}
                        onSelectedPalletChange={(id)=> setSelectedPalletId(id)}
                      />
                    </div>

                    {/* Controls Row: Import, Auto-Placement, Lock, Manifest, Export */}
                    <div className="mb-4">
                      {/* Mobile: 2-column grid, Desktop: flex row */}
                      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
                        {/* Import Dropdown */}
                        <div className="relative col-span-1" ref={importDropdownRef}>
                          <button
                            onClick={() => setShowImportDropdown(!showImportDropdown)}
                            className="w-full sm:w-auto h-11 px-3 sm:px-4 py-2 text-sm rounded-md border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 transition-colors font-medium flex items-center justify-center gap-1.5"
                          >
                            <Package size={16} className="sm:hidden" />
                            <span className="hidden sm:inline">Import pallets</span>
                            <span className="sm:hidden">Import</span>
                            <ChevronDown size={14} className={cn("transition-transform", showImportDropdown && "rotate-180")} />
                          </button>
                          
                          {/* Dropdown Menu */}
                          {showImportDropdown && (
                            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[200px] py-1">
                              <button
                                onClick={() => {
                                  setShowImportDropdown(false);
                                  sceneRef.current?.importStackedDataset?.('full');
                                }}
                                className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2.5 text-gray-700"
                              >
                                <Package size={16} className="text-emerald-600" />
                                <div>
                                  <div className="font-medium">Load Demo Pallets</div>
                                  <div className="text-xs text-gray-500">Import sample pallets</div>
                                </div>
                              </button>
                              <button
                                onClick={() => {
                                  setShowImportDropdown(false);
                                  importFileInputRef.current?.click();
                                }}
                                className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2.5 text-gray-700"
                              >
                                <FileSpreadsheet size={16} className="text-green-600" />
                                <div>
                                  <div className="font-medium">From Excel file</div>
                                  <div className="text-xs text-gray-500">Import .xlsx, .xls, or .csv</div>
                                </div>
                              </button>
                              <button
                                onClick={openScannerImportModal}
                                className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2.5 text-gray-700"
                              >
                                <ScanLine size={16} className="text-blue-600" />
                                <div>
                                  <div className="font-medium">From Pallet Scanner</div>
                                  <div className="text-xs text-gray-500">Import scanned pallets</div>
                                </div>
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Auto-placement */}
                        <button
                          onClick={() => sceneRef.current?.runStackedV2Placement?.()}
                          className="col-span-1 h-11 px-3 sm:px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-1.5"
                        >
                          <Truck size={16} className="sm:hidden" />
                          <span className="hidden sm:inline">Run auto-placement</span>
                          <span className="sm:hidden">Auto-place</span>
                        </button>
                        
                        {/* Lock Placed */}
                        <button
                          onClick={() => sceneRef.current?.lockAllInTruck?.()}
                          className="col-span-1 h-11 px-3 sm:px-4 py-2 text-sm rounded-md border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors font-medium flex items-center justify-center gap-1.5"
                        >
                          <Lock size={16} />
                          <span className="hidden sm:inline">Lock placed</span>
                          <span className="sm:hidden">Lock</span>
                        </button>
                        
                        {/* Open Manifest */}
                        <button
                          onClick={() => sceneRef.current?.openManifest?.()}
                          className="col-span-1 h-11 px-3 sm:px-4 py-2 text-sm rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors font-medium flex items-center justify-center gap-1.5"
                        >
                          <FileSpreadsheet size={16} className="sm:hidden" />
                          <span className="hidden sm:inline">Open manifest</span>
                          <span className="sm:hidden">Manifest</span>
                        </button>
                        
                        {/* Export PDF - full width on mobile */}
                        <button
                          onClick={() => sceneRef.current?.exportManifestPdf?.()}
                          className="col-span-2 sm:col-span-1 h-11 px-3 sm:px-4 py-2 text-sm rounded-md border border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100 transition-colors font-medium flex items-center justify-center gap-1.5"
                        >
                          <span>Export PDF</span>
                        </button>
                      </div>
                      <input
                        ref={importFileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files && e.target.files[0];
                          if (file) {
                            try { sceneRef.current?.importFromFile?.(file); } catch {}
                          }
                          e.target.value = "";
                        }}
                      />
                    </div>

                    {/* Pallet Scanner Import Modal */}
                    {showScannerImportModal && (
                      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl">
                          {/* Modal Header */}
                          <div className="flex items-center justify-between px-5 py-4 border-b">
                            <div>
                              <h2 className="text-lg font-semibold text-gray-900">Import from Pallet Scanner</h2>
                              <p className="text-sm text-gray-500">Select scanned pallets to add to truck</p>
                            </div>
                            <button
                              onClick={() => setShowScannerImportModal(false)}
                              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                              <X size={20} className="text-gray-500" />
                            </button>
                          </div>

                          {/* Search Bar */}
                          <div className="px-5 py-3 border-b bg-gray-50">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                type="text"
                                placeholder="Search by name, destination, shipment ID..."
                                value={scannerSearchQuery}
                                onChange={(e) => setScannerSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>

                          {/* Pallet List */}
                          <div className="flex-1 overflow-y-auto p-4">
                            {loadingScannedPallets ? (
                              <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                                <span className="ml-2 text-gray-500">Loading pallets...</span>
                              </div>
                            ) : scannedPallets.length === 0 ? (
                              <div className="text-center py-12">
                                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 font-medium">No scanned pallets found</p>
                                <p className="text-sm text-gray-400 mt-1">Use the Pallet Scanner to scan pallets first</p>
                              </div>
                            ) : (
                              (() => {
                                const filtered = scannedPallets.filter(p => {
                                  if (!scannerSearchQuery.trim()) return true;
                                  const q = scannerSearchQuery.toLowerCase();
                                  return (
                                    (p.name || '').toLowerCase().includes(q) ||
                                    (p.destination || '').toLowerCase().includes(q) ||
                                    (p.shipment_id || '').toLowerCase().includes(q) ||
                                    (p.sender || '').toLowerCase().includes(q)
                                  );
                                });
                                
                                return (
                                  <div className="space-y-2">
                                    {/* Select All */}
                                    <div className="flex items-center justify-between pb-2 mb-2 border-b">
                                      <span className="text-sm text-gray-600">
                                        {selectedScannedPallets.size} of {filtered.length} selected
                                      </span>
                                      <button
                                        onClick={() => {
                                          if (selectedScannedPallets.size === filtered.length) {
                                            setSelectedScannedPallets(new Set());
                                          } else {
                                            selectAllFiltered(filtered);
                                          }
                                        }}
                                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                                      >
                                        {selectedScannedPallets.size === filtered.length ? 'Deselect all' : 'Select all'}
                                      </button>
                                    </div>
                                    
                                    {filtered.map(pallet => (
                                      <div
                                        key={pallet.id}
                                        onClick={() => togglePalletSelection(pallet.id)}
                                        className={cn(
                                          "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                                          selectedScannedPallets.has(pallet.id)
                                            ? "bg-blue-50 border-blue-300 ring-1 ring-blue-200"
                                            : "bg-white border-gray-200 hover:bg-gray-50"
                                        )}
                                      >
                                        {/* Checkbox */}
                                        <div className={cn(
                                          "w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                                          selectedScannedPallets.has(pallet.id)
                                            ? "bg-blue-600 border-blue-600"
                                            : "border-gray-300"
                                        )}>
                                          {selectedScannedPallets.has(pallet.id) && (
                                            <Check size={14} className="text-white" />
                                          )}
                                        </div>

                                        {/* Pallet Info */}
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2">
                                            <span className="font-medium text-sm truncate">{pallet.name}</span>
                                            {pallet.attribute && pallet.attribute !== 'none' && (
                                              <span className={cn(
                                                "px-1.5 py-0.5 rounded text-xs font-medium",
                                                pallet.attribute === 'dangerous' ? "bg-red-100 text-red-700" :
                                                pallet.attribute === 'food' ? "bg-green-100 text-green-700" :
                                                pallet.attribute === 'medicine' ? "bg-blue-100 text-blue-700" :
                                                "bg-gray-100 text-gray-600"
                                              )}>
                                                {pallet.attribute}
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                                            <span className="flex items-center gap-1">
                                              <Ruler size={11} />
                                              {pallet.width_cm}×{pallet.depth_cm}×{pallet.height_cm} cm
                                            </span>
                                            <span className="flex items-center gap-1">
                                              <Weight size={11} />
                                              {pallet.weight_kg} kg
                                            </span>
                                          </div>
                                        </div>

                                        {/* Destination */}
                                        {pallet.destination && (
                                          <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded text-xs text-blue-700 flex-shrink-0">
                                            <MapPin size={11} />
                                            {pallet.destination}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                    
                                    {filtered.length === 0 && scannerSearchQuery && (
                                      <div className="text-center py-8 text-gray-500">
                                        No pallets match your search
                                      </div>
                                    )}
                                  </div>
                                );
                              })()
                            )}
                          </div>

                          {/* Modal Footer */}
                          <div className="flex items-center justify-between px-5 py-4 border-t bg-gray-50">
                            <button
                              onClick={() => setShowScannerImportModal(false)}
                              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={importSelectedPallets}
                              disabled={selectedScannedPallets.size === 0}
                              className={cn(
                                "px-5 py-2 text-sm rounded-lg font-medium transition-colors flex items-center gap-2",
                                selectedScannedPallets.size > 0
                                  ? "bg-blue-600 text-white hover:bg-blue-700"
                                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
                              )}
                            >
                              <Package size={16} />
                              Import {selectedScannedPallets.size} pallet{selectedScannedPallets.size !== 1 ? 's' : ''}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="text-xs text-gray-500 text-center mt-2">
                      Load pallets to generate manifest
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Loader App MVP */}
          {activeTab === 'loader' && (
            <div className="min-h-screen bg-gray-50/50">
              {/* Use new mobile-optimized loader instead of old DashboardHeader */}
              <NewLoaderPlanView />
            </div>
          )}

          {/* Driver Management */}
          {activeTab === "users" && (
            <div className="min-h-screen bg-gray-50/50">
              <DashboardHeader 
                title="Driver Management" 
                subtitle="Manage driver profiles and assignments"
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                user={user}
                isDark={isDark}
                onToggleTheme={() => setIsDark(d => !d)}
                onLogout={handleLogout}
              />
              
              <div className="p-6 space-y-8">
                {/* Stats Cards for Driver Management */}
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  <StatsCard
                    title="Total Drivers"
                    value={drivers.length}
                    change="Active in system"
                    changeType="neutral"
                    icon={Users}
                  />
                  
                  <StatsCard
                    title="Assigned Drivers"
                    value={assignedDriverIds.length}
                    change={`${assignedDriverIds.length}/${drivers.length} assigned`}
                    changeType="positive"
                    icon={Truck}
                  />
                  
                  <StatsCard
                    title="Active Trucks"
                    value={allTrucks.length}
                    change="Fleet capacity"
                    changeType="neutral"
                    icon={Truck}
                  />
                  
                  <StatsCard
                    title="Utilization"
                    value={`${drivers.length > 0 ? Math.round((assignedDriverIds.length / drivers.length) * 100) : 0}%`}
                    change="Driver efficiency"
                    changeType="positive"
                    icon={Gauge}
                  />
                </div>

                {/* Driver Management Content */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Driver Management</h3>
                      <p className="text-sm text-gray-600">Manage driver profiles and truck assignments</p>
                    </div>
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  
                  <div className="space-y-6">
                    {/* Add New Driver Form */}
                    <div className="grid gap-4 md:grid-cols-3">
                      <input
                        type="text"
                        value={newDriverName}
                        onChange={(e) => setNewDriverName(e.target.value)}
                        placeholder="Driver name"
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      />
                      
                      <input
                        type="text"
                        value={newDriverCerts}
                        onChange={(e) => setNewDriverCerts(e.target.value)}
                        placeholder="Certifications (comma separated)"
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      />
                      
                      <Button 
                        onClick={addDriver}
                        disabled={!newDriverName.trim()}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                      >
                        Add Driver
                      </Button>
                    </div>

                    {/* Driver List and Assignments */}
                    <div className="space-y-4">
                      {drivers.map((driver) => {
                        const assignedTruckId = Object.keys(assignments).find(tId => assignments[tId] === driver.id);
                        const assignedTruck = allTrucks.find(v => String(v.id) === String(assignedTruckId));
                        
                        return (
                          <div key={driver.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Users className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{driver.name}</p>
                                <p className="text-sm text-gray-600">
                                  {driver.certs && driver.certs.length > 0 ? driver.certs.join(', ') : 'No certifications'}
                                </p>
                                {assignedTruck && (
                                  <p className="text-xs text-green-600">Assigned to: {assignedTruck.name || assignedTruck.id}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <select
                                value={assignments[assignedTruckId] === driver.id ? assignedTruckId : ""}
                                onChange={(e) => {
                                  if (assignedTruckId) {
                                    assignDriverToTruck(assignedTruckId, "");
                                  }
                                  if (e.target.value) {
                                    assignDriverToTruck(e.target.value, driver.id);
                                  }
                                }}
                                className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                              >
                                <option value="">Assign to truck</option>
                                {allTrucks.filter(truck => !assignments[truck.id] || assignments[truck.id] === driver.id).map((truck) => (
                                  <option key={truck.id} value={truck.id}>
                                    {truck.name || truck.id}
                                  </option>
                                ))}
                              </select>
                              <Button
                                onClick={() => removeDriver(driver.id)}
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                      
                      {drivers.length === 0 && (
                        <div className="text-center py-8">
                          <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-gray-500">No drivers registered</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Fleet Management */}
          {activeTab === "products" && (
            <div className="min-h-screen bg-gray-50/50">
              <DashboardHeader 
                title="Fleet Management" 
                subtitle="Manage vehicles and their connections"
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                user={user}
                isDark={isDark}
                onToggleTheme={() => setIsDark(d => !d)}
                onLogout={handleLogout}
              />
              
              <div className="p-6 space-y-8">
                {/* Fleet Stats Cards */}
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  <StatsCard
                    title="Total Vehicles"
                    value={allTrucks.length}
                    change="Active fleet"
                    changeType="neutral"
                    icon={Truck}
                  />
                  
                  <StatsCard
                    title="Solo Trucks"
                    value={trucksOnly.length}
                    change={`${trucksOnly.length}/${allTrucks.length} vehicles`}
                    changeType="neutral"
                    icon={Truck}
                  />
                  
                  <StatsCard
                    title="Trailers"
                    value={trailersOnly.length}
                    change={`${trailersOnly.length}/${allTrucks.length} vehicles`}
                    changeType="neutral"
                    icon={Truck}
                  />
                  
                  <StatsCard
                    title="Connections"
                    value={connections.length}
                    change="Truck-trailer pairs"
                    changeType="positive"
                    icon={Settings}
                  />
                </div>

                {/* Fleet Management Sections */}
                <div className="grid gap-6 lg:grid-cols-2">
                  {/* Create Connection */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Connect Vehicles</h3>
                        <p className="text-sm text-gray-600">Create truck-trailer combinations</p>
                      </div>
                      <Settings className="w-5 h-5 text-blue-600" />
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Select Truck</label>
                        <select
                          value={connectTruckId}
                          onChange={(e) => setConnectTruckId(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        >
                          <option value="">Choose a truck...</option>
                          {trucksOnly.map((truck) => (
                            <option key={truck.id} value={truck.id}>
                              {labelForVehicle(truck)} (Solo Truck)
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Select Trailer</label>
                        <select
                          value={connectTrailerId}
                          onChange={(e) => setConnectTrailerId(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        >
                          <option value="">Choose a trailer...</option>
                          {trailersOnly.map((trailer) => (
                            <option key={trailer.id} value={trailer.id}>
                              {labelForVehicle(trailer)} (Trailer)
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <Button 
                        onClick={addConnection}
                        disabled={!connectTruckId || !connectTrailerId}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                      >
                        Create Connection
                      </Button>
                    </div>
                  </div>

                  {/* Active Connections */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Active Connections</h3>
                        <p className="text-sm text-gray-600">Current truck-trailer pairs</p>
                      </div>
                      <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                        {connections.length} active
                      </span>
                    </div>
                    
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {connections.length > 0 ? (
                        connections.map((conn) => {
                          const truck = allTrucks.find((v) => String(v.id) === String(conn.truckId));
                          const trailer = allTrucks.find((v) => String(v.id) === String(conn.trailerId));
                          
                          return (
                            <div key={conn.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                  <Settings className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {labelForVehicle(truck)} + {labelForVehicle(trailer)}
                                  </p>
                                  <p className="text-sm text-gray-600">Truck-Trailer Connection</p>
                                </div>
                              </div>
                              <Button
                                onClick={() => removeConnection(conn.id)}
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                              >
                                Disconnect
                              </Button>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-8">
                          <Settings className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-gray-500">No connections created</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Fleet Overview */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Fleet Overview</h3>
                      <p className="text-sm text-gray-600">All vehicles in your fleet</p>
                    </div>
                    <Truck className="w-5 h-5 text-blue-600" />
                  </div>
                  
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {allTrucks.map((vehicle) => (
                      <div key={vehicle.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <Truck className="w-4 h-4 text-gray-600" />
                            <span className="font-medium text-gray-900">{labelForVehicle(vehicle)}</span>
                          </div>
                          <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
                            {vehicle.vehicleType || vehicle.type || 'trailer'}
                          </span>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Pallets:</span>
                            <span className="font-medium">{(vehicle.pallets || []).length}</span>
                          </div>
                          
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Driver:</span>
                            <span className="font-medium">
                              {assignments[vehicle.id] ? 
                                drivers.find(d => d.id === assignments[vehicle.id])?.name || 'Unknown' :
                                'Unassigned'
                              }
                            </span>
                          </div>
                          
                          {connections.find(c => c.truckId === vehicle.id || c.trailerId === vehicle.id) && (
                            <div className="mt-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Connected
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Feedback Content */}
          {activeTab === 'feedback' && (
            <div className="p-6">
              <div className="max-w-5xl mx-auto">
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="md:col-span-2">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6">
                      <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Send Feedback</h2>
                      <FeedbackFormInline />
                    </div>
                  </div>
                  <div className="md:col-span-1 space-y-6">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-4 text-sm leading-relaxed">
                      <h3 className="font-medium mb-2 text-gray-700 dark:text-gray-200">Why Feedback?</h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-3">Help prioritize stability, performance, and features. Include steps to reproduce problems if reporting a bug.</p>
                      <p className="text-gray-500 dark:text-gray-500 text-xs">Ratings & tags guide roadmap priorities. Email is optional.</p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6">
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <p className="text-sm">Admin feedback panel will be available soon.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Coming Soon: Route Planning */}
          {activeTab === 'coming-route' && (
            <div className="min-h-screen bg-gray-50/50">
              <DashboardHeader 
                title="Route Planning"
                subtitle="Coming soon"
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                user={user}
                isDark={isDark}
                onToggleTheme={() => setIsDark(d => !d)}
                onLogout={handleLogout}
              />
              <div className="p-6">
                <div className="max-w-3xl mx-auto bg-white rounded-xl border border-gray-200 p-10 text-center">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-3">Route Planning</h2>
                  <p className="text-gray-600 mb-6">We’re building powerful tools for multi-stop routing, time windows, and fuel-efficient planning.</p>
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-sm font-medium">Coming soon</div>
                </div>
              </div>
            </div>
          )}

          {/* Pallet Scanner Mobile MVP */}
          {activeTab === 'pallet-scanner' && (
            <div className="min-h-screen bg-gray-50/50">
              <PalletScannerPage />
            </div>
          )}

          {/* Pallet Classification AI Actual Page */}
          {activeTab === 'pallet-classification' && (
            <div className="min-h-screen bg-gray-50/50">
              <PalletClassificationPage />
            </div>
          )}
          </main>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
}

// default export already declared above
